import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { V2_BASE } from '../config';
import {
  Home, ChevronRight, Server, Search, Download,
  LayoutGrid, MoreVertical, ShoppingCart, ChevronDown, Plus, ArrowUpDown,
  Power, RotateCcw, RefreshCw, Shield, Camera, Key, Terminal,
  Globe, Wrench, ArrowRightLeft, ArrowRight, Trash2, Pencil, Settings2, Maximize2, X,
  Copy, ChevronUp, Receipt, PlusCircle,
} from 'lucide-react';
import { EMPTY_ILLU } from '../illu';
import './contabilita.css';
import './hosting.css';
import './cloud.css';

const api = axios.create({ withCredentials: true });

const TAG_PALETTE = ['#30abff', '#16a36a', '#ff8500', '#d9364f', '#d9364f', '#86b2d1', '#7986cb', '#00bcd4', '#8e8e8e'];

// Applicazioni comuni per il firewall (porta 25 SMTP esclusa: non apribile per policy)
const FW_APPS = [
  { name: 'HTTP', proto: 'TCP', ports: [80] },
  { name: 'HTTPS', proto: 'TCP', ports: [443] },
  { name: 'SSH', proto: 'TCP', ports: [22] },
  { name: 'RDP', proto: 'TCP', ports: [3389] },
  { name: 'FTP', proto: 'TCP', ports: [21] },
  { name: 'DNS', proto: 'UDP', ports: [53] },
  { name: 'MySQL', proto: 'TCP', ports: [3306] },
  { name: 'PostgreSQL', proto: 'TCP', ports: [5432] },
  { name: 'Mail (SMTP SSL)', proto: 'TCP', ports: [465] },
  { name: 'Mail (SMTP TLS)', proto: 'TCP', ports: [587] },
  { name: 'Mail (IMAP)', proto: 'TCP', ports: [143] },
  { name: 'Mail (IMAP SSL)', proto: 'TCP', ports: [993] },
  { name: 'Mail (POP3)', proto: 'TCP', ports: [110] },
  { name: 'Mail (POP3 SSL)', proto: 'TCP', ports: [995] },
  { name: 'VPN (OpenVPN)', proto: 'UDP', ports: [1194] },
  { name: 'VPN (WireGuard)', proto: 'UDP', ports: [51820] },
  { name: 'NTP', proto: 'UDP', ports: [123] },
  { name: 'SNMP', proto: 'UDP', ports: [161] },
];
// Mappa porta → nome applicazione, per derivare la "Descrizione" delle regole firewall.
const FW_PORT_NAME = FW_APPS.reduce((m, a) => { a.ports.forEach((p) => { if (!m[p]) m[p] = a.name; }); return m; }, {});
const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: '2-digit' }); } catch { return '—'; } };
const dMinus = (d) => { if (!d) return null; try { const ms = new Date(d).getTime() - Date.now(); return Math.ceil(ms / 86400000); } catch { return null; } };

/* Select stile material (sottolineato + chevron) come il riferimento reinstall */
const MatSelect = ({ value, onChange, options, disabled, placeholder }) => (
  <div className={`nfx-matsel${disabled ? ' is-disabled' : ''}`}>
    <select className="nfx-matsel__sel" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
      {placeholder && <option value="" disabled>{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span className="nfx-matsel__chevron"><ChevronDown size={16} /></span>
  </div>
);
const STATE_TAG = (raw) => {
  const s = (raw || '').toLowerCase();
  if (['attivo', 'active', 'running', 'ok', 'valido', 'start', 'avviato'].includes(s)) return { cls: 'ok', label: 'Avviato' };
  if (['scaduto', 'sospeso', 'stopped', 'errore', 'error', 'fermo', 'stop'].includes(s)) return { cls: 'danger', label: raw || 'Fermo' };
  if (['in_attesa', 'pending', 'provisioning', 'in_lavorazione'].includes(s)) return { cls: 'warn', label: 'In attesa' };
  return { cls: 'info', label: raw || '—' };
};
const NavIcon = ({ name, size = 24 }) => (
  <img className="nfx-nav-ico" src={`/nav/${name}.svg`} alt="" aria-hidden="true" width={size} height={size} />
);
const parseMeta = (m) => { if (!m) return {}; if (typeof m === 'string') { try { return JSON.parse(m); } catch { return {}; } } return m; };
const prettyOs = (s) => {
  if (!s) return '—';
  const str = String(s).trim();
  // Slug tecnico: "ubuntu-24.04.3-x86_64" → "Ubuntu 24.04.3 (x86_64)"
  if (/^[a-z]+[-_]\d/i.test(str)) {
    const mm = str.match(/^([a-z]+)[-_]?([\d.]+)?[-_]?(x86_64|amd64|arm64|aarch64)?/i);
    if (mm) {
      const name = mm[1].charAt(0).toUpperCase() + mm[1].slice(1);
      return [name, mm[2], mm[3] ? `(${mm[3]})` : ''].filter(Boolean).join(' ');
    }
  }
  // Etichetta già leggibile (es. "Ubuntu 26.04 LTS"): deduplica parole ripetute consecutive
  const words = str.split(/\s+/);
  const out = [];
  words.forEach((w) => { if (!out.length || out[out.length - 1].toLowerCase() !== w.toLowerCase()) out.push(w); });
  return out.join(' ');
};
const vpsInfo = (v) => {
  const m = parseMeta(v.metadata);
  const cred = v.credenziali || {};
  const cpu = m.cores || m.cpu || m.vcpu || null;
  const ramGb = m.ram_gb || m.memory || m.ram || null;
  const diskGb = m.disk_gb || m.disk || m.disco || m.storage || null;
  // Offerta commerciale dal prefisso serie del piano: L* → VPS Lite, C* → VPS Cloud
  const planRaw = m.piano || m.plan_name || m.prodotto || m.plan || m.offerta || '';
  const serie = String(planRaw).trim().charAt(0).toUpperCase();
  const offerta = serie === 'L' ? 'VPS Lite' : serie === 'C' ? 'VPS Cloud' : (planRaw || 'VPS');
  return {
    os: prettyOs(m.os || m.image || m.sistema_operativo || v.sistema_operativo),
    plan: offerta,
    hostname: m.hostname || m.host_name || v.provider_resource_name || v.nome_servizio || '—',
    ipv4: cred.ipv4 || v.ip_address || m.ipv4 || '—',
    ipv6: cred.ipv6 || m.ipv6 || '—',
    ssh: cred.ssh_user || cred.username || 'root',
    cpu: cpu ? `${cpu} vCPU` : null,
    ram: ramGb ? `${ramGb} GB` : null,
    disk: diskGb ? `${diskGb} GB` : null,
    datacenter: m.datacenter || v.datacenter || null,
    rootpw: cred.root_password || null,
  };
};

const dedInfo = (v) => {
  const m = parseMeta(v.metadata);
  return {
    plan: m.piano || m.prodotto || m.server_type || 'Server dedicato',
    hostname: m.hostname || v.nome_servizio || '—',
    cpu: m.cpu || null,
    ram: m.ram || (m.ram_gb ? `${m.ram_gb} GB` : null),
    disk: m.disco || m.disk || null,
    ipv4: v.ip_address || m.ipv4 || '—',
    datacenter: m.datacenter || v.datacenter || null,
  };
};

const TABS = [
  { key: 'dedicated', label: 'Server Dedicati', path: '/cloud-dedicated/dedicated', icon: 'nav-server-cloud' },
  { key: 'vps',       label: 'VPS',             path: '/cloud-dedicated/vps',       icon: 'nav-vps-cloud' },
  { key: 'storage',   label: 'Storage',         path: '/cloud-dedicated/storage',   icon: 'nav-storage' },
  { key: 'nas',       label: 'NAS Synology',    path: '/cloud-dedicated/nas',       icon: 'nav-nas-synology' },
  { key: 'housing',   label: 'Housing',         path: '/cloud-dedicated/housing',   icon: 'nav-housing' },
];

const IlluEmpty = ({ title, desc, primary, onPrimary, onLink }) => (
  <div className="nfx-card">
    <div className="nfx-hd-illu">
      <div className="nfx-hd-illu__l">
        <div className="nfx-hd-illu__title">{title}</div>
        <div className="nfx-hd-illu__desc">{desc}</div>
        <div className="nfx-hd-illu__actions">
          <button className="nfx-btn-blue" onClick={onPrimary}>{primary}</button>
          <button className="nfx-hd-illu__link" onClick={onLink}>Scopri le nostre offerte</button>
        </div>
      </div>
      <div className="nfx-hd-illu__illu"><img src={EMPTY_ILLU} alt="" /></div>
    </div>
  </div>
);

const CloudDashboard = ({ tab }) => {
  const navigate = useNavigate();
  const go = (p) => navigate(V2_BASE + p);
  const offer = () => window.open('https://neoflux.it', '_blank');
  const [vps, setVps] = useState([]);
  const [dedicated, setDedicated] = useState([]);
  const [servizi, setServizi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowMenu, setRowMenu] = useState(null);
  const [gestOpen, setGestOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState('');
  const [vpsView, setVpsView] = useState('list'); // list | card
  const [tagsByVps, setTagsByVps] = useState({});
  const [tagModal, setTagModal] = useState(null); // { vpsId, view:'select'|'create', name, color }
  const openTagModal = (vpsId) => setTagModal({ vpsId, view: 'select', name: '', color: TAG_PALETTE[0] });
  const persistTags = (vpsId, tags) => api.put(`/api/cloud/vps/${vpsId}/tags`, { tags }).catch(() => {});
  const addTag = () => {
    const m = tagModal;
    if (!m) return;
    const name = (m.name || '').trim();
    if (!name) return;
    const cur = tagsByVps[m.vpsId] || [];
    if (cur.some((t) => t.name.toLowerCase() === name.toLowerCase())) { setTagModal({ ...m, view: 'select', name: '' }); return; }
    const next = [...cur, { name, color: m.color }];
    setTagsByVps((prev) => ({ ...prev, [m.vpsId]: next }));
    persistTags(m.vpsId, next);
    setTagModal({ ...m, view: 'select', name: '' });
  };
  const removeTag = (vpsId, name) => {
    const next = (tagsByVps[vpsId] || []).filter((t) => t.name !== name);
    setTagsByVps((prev) => ({ ...prev, [vpsId]: next }));
    persistTags(vpsId, next);
  };
  const gestRef = useRef(null);
  useEffect(() => {
    if (!gestOpen) return undefined;
    const onDown = (e) => { if (gestRef.current && !gestRef.current.contains(e.target)) setGestOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [gestOpen]);

  useEffect(() => {
    let alive = true; setLoading(true);
    Promise.allSettled([api.get('/api/cloud/vps'), api.get('/api/cloud/dedicated'), api.get('/api/servizi/riepilogo')])
      .then(([v, d, s]) => {
        if (!alive) return;
        const vpsList = v.status === 'fulfilled' && Array.isArray(v.value.data) ? v.value.data : [];
        setVps(vpsList);
        const tmap = {};
        vpsList.forEach((x) => { const t = parseMeta(x.metadata).tags; if (Array.isArray(t) && t.length) tmap[x.id] = t; });
        setTagsByVps(tmap);
        setDedicated(d.status === 'fulfilled' && Array.isArray(d.value.data) ? d.value.data : []);
        setServizi(s.status === 'fulfilled' ? (s.value.data?.servizi || []) : []);
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filterSvc = (re) => servizi.filter(x => re.test(`${x.tipo_prodotto || ''} ${x.categoria || ''} ${x.nome || ''}`));
  const cur = TABS.find(t => t.key === tab) || TABS[0];

  const vpsFiltered = (() => {
    const s = q.trim().toLowerCase();
    if (!s) return vps;
    return vps.filter((v) => {
      const info = vpsInfo(v);
      return `${v.nome_servizio || ''} ${info.plan} ${info.hostname} ${info.os}`.toLowerCase().includes(s);
    });
  })();

  const exportCsv = () => {
    const rows = [['Nome servizio', 'Offerta', 'Hostname', 'Sistema operativo', 'Scadenza']];
    vps.forEach((v) => {
      const info = vpsInfo(v);
      rows.push([v.nome_servizio || '', info.plan || '', info.hostname || '', info.os || '', v.data_scadenza ? fmtDate(v.data_scadenza) : '']);
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'vps.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const rightIcons = (
    <div className="nfx-tb__actions">
      <button className="nfx-iconbtn"><Search size={18} /></button>
      <button className="nfx-iconbtn"><Download size={18} /></button>
      <button className="nfx-iconbtn is-active"><LayoutGrid size={18} /></button>
    </div>
  );

  /* Tabella generica prodotto (Server/Storage/NAS/Housing) */
  const genericTable = (list, unit) => (
    <div className="nfx-card">
      <div className="nfx-tb">
        <span className="nfx-tb__count"><b>{list.length}</b> {unit}{list.length === 1 ? '' : 'i'}</span>
        <button className="nfx-tb__btn" onClick={offer}><ShoppingCart size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />Ordinare</button>
        <button className="nfx-tb__btn nfx-tb__btn--ghost">Gestire <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /></button>
        <span className="nfx-tb__spacer" />{rightIcons}
      </div>
      <table className="nfx-table">
        <thead><tr><th className="nfx-td-ico" /><th>Nome</th><th>Stato</th><th>Scadenza</th><th>Tag</th><th className="nfx-td-actions" /></tr></thead>
        <tbody>
          {list.map((s, i) => {
            const st = STATE_TAG(s.stato || s.stato_provisioning);
            return (
              <tr key={s.id || i}>
                <td className="nfx-td-ico"><NavIcon name={cur.icon} size={26} /></td>
                <td className="nfx-cell-strong">{s.nome || s.nome_prodotto || s.nome_servizio || '—'}</td>
                <td><span className={`nfx-st nfx-st--${st.cls}`}>{st.label}</span></td>
                <td>{s.data_scadenza ? `Scade il ${fmtDate(s.data_scadenza)}` : '—'}</td>
                <td>—</td>
                <td className="nfx-td-actions"><button className="nfx-iconbtn"><MoreVertical size={16} /></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderTab = () => {
    if (loading) return <div className="nfx-card"><div className="nfx-loading">Caricamento…</div></div>;

    if (tab === 'vps') {
      if (vps.length === 0) return <IlluEmpty title="Non hai nessun VPS"
        desc="Distribuisci server virtuali Cloud in pochi minuti, con root access completo e risorse dedicate."
        primary="Ordina un VPS" onPrimary={offer} onLink={offer} />;
      return (
        <div className="nfx-card nfx-vps-listcard">
          <div className="nfx-vps-toolbar">
            <span className="nfx-tb__count"><b>{vps.length}</b> <span className="nfx-tb__count-lbl">VPS</span></span>
            <button className="nfx-vps-order" onClick={offer}><ShoppingCart size={16} /><span>Ordinare</span></button>
            <div className="nfx-vps-manage" ref={gestRef}>
              <button className="nfx-vps-manage-btn" onClick={() => setGestOpen((o) => !o)}><span>Gestire</span><ChevronDown size={14} /></button>
              {gestOpen && (
                <div className="nfx-vps-manage-menu">
                  <button onClick={() => { setGestOpen(false); go('/billing/fatture'); }}><Receipt size={16} /> Storico delle fatture</button>
                  <button onClick={() => { setGestOpen(false); offer(); }}><ArrowRightLeft size={16} /> Trasferisci più prodotti su un altro account</button>
                  <button onClick={() => { setGestOpen(false); offer(); }}><Key size={16} /> Mazzo di chiavi</button>
                </div>
              )}
            </div>
            <span className="nfx-tb__spacer" />
            <div className="nfx-vps-tools">
              <div className={`nfx-vps-search${searchOpen ? ' is-open' : ''}`}>
                <button className="nfx-vps-tool" title="Ricerca" aria-label="Ricerca" onClick={() => { setSearchOpen((o) => !o); if (searchOpen) setQ(''); }}><Search size={16} /></button>
                {searchOpen && (
                  <>
                    <input autoFocus placeholder="Ricerca" value={q} onChange={(e) => setQ(e.target.value)} />
                    <button className="nfx-vps-search-clear" title="Cancella" aria-label="Cancella" onClick={() => { setQ(''); setSearchOpen(false); }}><X size={14} /></button>
                  </>
                )}
              </div>
              <button className="nfx-vps-tool" title="Scaricare la lista nel formato CSV" aria-label="Scaricare la lista nel formato CSV" onClick={exportCsv}><Download size={16} /></button>
              <button className={`nfx-vps-tool nfx-vps-tool--plain${vpsView === 'card' ? ' is-active' : ''}`} title={vpsView === 'card' ? 'Modifica nella vista elenco' : 'Modifica nella vista scheda'} aria-label="Modifica nella vista scheda" onClick={() => setVpsView((v) => (v === 'card' ? 'list' : 'card'))}><LayoutGrid size={20} /></button>
            </div>
          </div>
          {vpsView === 'card' ? (
            <div className="nfx-vps-cards">
              {vpsFiltered.map((v, i) => {
                const info = vpsInfo(v); const dm = dMinus(v.data_scadenza);
                return (
                  <div className="nfx-vps-cardc" key={v.id || i}>
                    <div className="nfx-vps-cardc__body">
                      <div className="nfx-vps-cardc__bodyin">
                        <a className="nfx-vps-cardc__elements" onClick={() => go(`/cloud-dedicated/vps/${v.numero}`)}>
                          <div className="nfx-vps-cardc__thumb"><NavIcon name="nav-vps-cloud" size={16} /></div>
                          <div className="nfx-vps-cardc__det">
                            <div className="nfx-vps-cardc__name">
                              <div className="nfx-vps-cardc__pname" title={v.nome_servizio || ''}>{v.nome_servizio || '—'}</div>
                              <div className="nfx-vps-cardc__sub">{info.plan} - {info.hostname}</div>
                            </div>
                            <div className="nfx-vps-cardc__dist">Distribuzione: {info.os}</div>
                            <div className="nfx-vps-cardc__exp">Scade il {fmtDate(v.data_scadenza)}{dm != null && dm >= 0 && <span className="nfx-dom-dminus"> (G-{dm})</span>}</div>
                          </div>
                        </a>
                        <div className="nfx-vps-cardc__act" onClick={(e) => e.stopPropagation()}>
                          <div className="nfx-cp-gest">
                            <button className="nfx-iconbtn" onClick={() => setRowMenu(rowMenu === v.id ? null : v.id)}><MoreVertical size={16} /></button>
                            {rowMenu === v.id && (
                              <div className="nfx-cp-gest__menu" style={{ right: 8 }} onMouseLeave={() => setRowMenu(null)}>
                                <button onClick={() => go(`/cloud-dedicated/vps/${v.numero}`)}><Settings2 size={15} /> Pannello di controllo</button>
                                <button onClick={() => go('/billing/rinnovo')}><ArrowUpDown size={15} /> Modifica l&rsquo;offerta</button>
                                <button onClick={() => go('/billing/rinnovo')}><RefreshCw size={15} /> Rinnova</button>
                                <button onClick={offer}><ArrowRightLeft size={15} /> Trasferisci a un altro account</button>
                                <button onClick={offer} className="nfx-cp-gest__danger"><Trash2 size={15} /> Termina</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="nfx-vps-cardc__foot">
                      {(tagsByVps[v.id] || []).map((t, ti) => (
                        <span key={ti} className="nfx-vps-tagchip" style={{ background: t.color }}>{t.name}</span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <div className="nfx-vps-tablewrap">
          <table className="nfx-table nfx-vps-table">
            <thead><tr>
              <th className="nfx-td-ico" />
              <th>Nome <ArrowUpDown size={13} className="nfx-th-sort" /></th>
              <th>Sistema operativo</th>
              <th>Scadenza <ArrowUpDown size={13} className="nfx-th-sort" /></th>
              <th>Tag</th>
              <th className="nfx-td-actions" />
            </tr></thead>
            <tbody>
              {vpsFiltered.map((v, i) => {
                const info = vpsInfo(v);
                const dm = dMinus(v.data_scadenza);
                return (
                  <tr key={v.id || i} className="is-clickable" onClick={() => go(`/cloud-dedicated/vps/${v.numero}`)}>
                    <td className="nfx-td-ico"><NavIcon name="nav-vps-cloud" size={26} /></td>
                    <td>
                      <div className="nfx-cell-strong">{v.nome_servizio || '—'}</div>
                      <div className="nfx-cell-muted">{info.plan} - {info.hostname}</div>
                    </td>
                    <td className="nfx-cell-muted">{info.os}</td>
                    <td>Scade il {fmtDate(v.data_scadenza)}{dm != null && dm >= 0 && <span className="nfx-dom-dminus"> (G-{dm})</span>}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="nfx-vps-tags">
                        <button className="nfx-vps-tagadd" aria-label="Aggiungi un tag" title="Gestisci i tag" onClick={() => openTagModal(v.id)}><PlusCircle size={16} /></button>
                        {(tagsByVps[v.id] || []).map((t, ti) => (
                          <span key={ti} className="nfx-vps-tagchip" style={{ background: t.color }}>{t.name}</span>
                        ))}
                      </div>
                    </td>
                    <td className="nfx-td-actions" onClick={(e) => e.stopPropagation()}>
                      <div className="nfx-cp-gest">
                        <button className="nfx-iconbtn" onClick={() => setRowMenu(rowMenu === v.id ? null : v.id)}><MoreVertical size={16} /></button>
                        {rowMenu === v.id && (
                          <div className="nfx-cp-gest__menu" style={{ right: 8 }} onMouseLeave={() => setRowMenu(null)}>
                            <button onClick={() => go(`/cloud-dedicated/vps/${v.numero}`)}><Settings2 size={15} /> Pannello di controllo</button>
                            <button onClick={() => go('/billing/rinnovo')}><ArrowUpDown size={15} /> Modifica l&rsquo;offerta</button>
                            <button onClick={() => go('/billing/rinnovo')}><RefreshCw size={15} /> Rinnova</button>
                            <button onClick={offer}><ArrowRightLeft size={15} /> Trasferisci a un altro account</button>
                            <button onClick={offer} className="nfx-cp-gest__danger"><Trash2 size={15} /> Termina</button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          )}
        </div>
      );
    }

    if (tab === 'dedicated') {
      if (dedicated.length === 0) return <IlluEmpty title="Non hai nessun server dedicato"
        desc="Infrastruttura Bare Metal ad alte prestazioni interamente dedicata alla tua azienda."
        primary="Ordina un server" onPrimary={offer} onLink={offer} />;
      return (
        <div className="nfx-card">
          <div className="nfx-tb">
            <span className="nfx-tb__count"><b>{dedicated.length}</b> Server</span>
            <button className="nfx-btn-blue" onClick={offer}><ShoppingCart size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />Ordina</button>
            <button className="nfx-tb__btn nfx-tb__btn--ghost">Gestisci <ChevronDown size={14} style={{ verticalAlign: '-2px' }} /></button>
            <span className="nfx-tb__spacer" />{rightIcons}
          </div>
          <table className="nfx-table nfx-vps-table">
            <thead><tr>
              <th className="nfx-td-ico" />
              <th>Nome <ArrowUpDown size={13} className="nfx-th-sort" /></th>
              <th>Configurazione</th>
              <th>Datacenter</th>
              <th>Scadenza <ArrowUpDown size={13} className="nfx-th-sort" /></th>
              <th className="nfx-td-actions" />
            </tr></thead>
            <tbody>
              {dedicated.map((d, i) => {
                const info = dedInfo(d);
                const st = STATE_TAG(d.stato);
                const dm = dMinus(d.data_scadenza);
                return (
                  <tr key={d.id || i} className="is-clickable" onClick={() => go(`/cloud-dedicated/dedicated/${d.id}`)}>
                    <td className="nfx-td-ico"><NavIcon name="nav-server-cloud" size={26} /></td>
                    <td>
                      <div className="nfx-cell-strong">{d.nome_servizio || '—'}</div>
                      <div className="nfx-cell-muted"><span className={`nfx-st nfx-st--${st.cls}`}>{st.label}</span></div>
                    </td>
                    <td className="nfx-cell-muted">{info.plan}</td>
                    <td className="nfx-cell-muted">{info.datacenter || '—'}</td>
                    <td>{fmtDate(d.data_scadenza)}{dm != null && dm >= 0 && <span className="nfx-dom-dminus"> (D-{dm})</span>}</td>
                    <td className="nfx-td-actions" onClick={(e) => e.stopPropagation()}><button className="nfx-iconbtn" onClick={() => go(`/cloud-dedicated/dedicated/${d.id}`)}><MoreVertical size={16} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    if (tab === 'storage') {
      const list = filterSvc(/storage|backup|swiss|s3|object/i);
      return list.length ? genericTable(list, 'Storage') : <IlluEmpty title="Nessuno spazio di archiviazione"
        desc="Storage cloud sicuro e scalabile per backup, archivi e oggetti (S3)."
        primary="Ordina Storage" onPrimary={offer} onLink={offer} />;
    }
    if (tab === 'nas') {
      const list = filterSvc(/nas|synology/i);
      return list.length ? genericTable(list, 'NAS') : <IlluEmpty title="Non hai nessun NAS Synology"
        desc="NAS Synology gestiti e ospitati nei nostri datacenter, per un backup e uno storage privato affidabile."
        primary="Ordina un NAS" onPrimary={offer} onLink={offer} />;
    }
    // housing
    const list = filterSvc(/housing|colocation|rack/i);
    return list.length ? genericTable(list, 'Housing') : <IlluEmpty title="Nessun servizio di Housing"
      desc="Ospita i tuoi server nei nostri datacenter con housing e colocation sicuri e ridondati."
      primary="Richiedi Housing" onPrimary={offer} onLink={offer} />;
  };

  return (
    <div>
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => go('')} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={12} /></span>
        <span className="nfx-crumb__cur"><Server size={16} style={{ color: '#1d3c51' }} />Cloud e Server Dedicati</span>
      </div>
      <h1 className="nfx-cb-title">Dashboard Cloud e Server Dedicati</h1>

      <div className="nfx-hdtabs">
        {TABS.map(t => (
          <button key={t.key} className={`nfx-hdtab${tab === t.key ? ' is-active' : ''}`} onClick={() => go(t.path)}>{t.label}</button>
        ))}
      </div>

      {renderTab()}

      {tagModal && (
        <div className="nfx-cfm-scrim" onClick={() => setTagModal(null)}>
          <div className="nfx-tagm" onClick={(e) => e.stopPropagation()}>
            {tagModal.view === 'select' ? (
              <>
                <div className="nfx-tagm__head">Etichetta</div>
                <div className="nfx-tagm__body">
                  <div className="nfx-tagm__field">
                    {(tagsByVps[tagModal.vpsId] || []).map((t, ti) => (
                      <span key={ti} className="nfx-vps-tagchip" style={{ background: t.color }}>
                        {t.name}
                        <button className="nfx-tagm__chipx" onClick={() => removeTag(tagModal.vpsId, t.name)} aria-label="Rimuovi"><X size={12} /></button>
                      </span>
                    ))}
                    <input className="nfx-tagm__input" autoFocus placeholder="Seleziona o crea un tag"
                      value={tagModal.name}
                      onChange={(e) => setTagModal((m) => ({ ...m, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter' && tagModal.name.trim()) setTagModal((m) => ({ ...m, view: 'create' })); }} />
                  </div>
                  {tagModal.name.trim() && (
                    <button className="nfx-tagm__createlink" onClick={() => setTagModal((m) => ({ ...m, view: 'create' }))}>
                      <PlusCircle size={15} /> Crea il tag «{tagModal.name.trim()}»
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="nfx-tagm__head">Crea un tag</div>
                <div className="nfx-tagm__body">
                  <div className="nfx-tagm__create">
                    <div className="nfx-tagm__colors">
                      {TAG_PALETTE.map((c) => (
                        <button key={c} className={`nfx-tagm__swatch${tagModal.color === c ? ' is-sel' : ''}`} style={{ background: c }}
                          onClick={() => setTagModal((m) => ({ ...m, color: c }))} aria-label={c} />
                      ))}
                    </div>
                    <input className="nfx-tagm__input" autoFocus placeholder="Nome del tag"
                      value={tagModal.name}
                      onChange={(e) => setTagModal((m) => ({ ...m, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') addTag(); }} />
                  </div>
                </div>
                <div className="nfx-tagm__foot">
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => setTagModal((m) => ({ ...m, view: 'select' }))}>Annullare</button>
                  <button className="nfx-cfm-btn nfx-cfm-btn--primary" onClick={addTag} disabled={!tagModal.name.trim()}>Crea</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ Console noVNC (self-hosted, nessun pannello) ═══════════════════ */
const VncConsole = ({ id, name, onClose }) => {
  const screenRef = useRef(null);
  const modalRef = useRef(null);
  const rfbRef = useRef(null);
  const [phase, setPhase] = useState('connecting'); // connecting | connected | error
  const [status, setStatus] = useState('Connessione alla console…');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setPhase('connecting'); setStatus('Connessione alla console…');
    (async () => {
      try {
        const { data } = await api.get(`/api/cloud/vps/${id}/vnc?connect=1`);
        if (cancelled) return;
        if (!data?.enabled || !data.ticket) { setPhase('error'); setStatus('VNC non abilitata su questo server.'); return; }
        const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
        const url = `${proto}://${window.location.host}/api/cloud/vps/vnc-ws?ticket=${encodeURIComponent(data.ticket)}`;
        const { default: RFB } = await import('@novnc/novnc');
        if (cancelled || !screenRef.current) return;
        const rfb = new RFB(screenRef.current, url, { credentials: { password: data.password || '' } });
        rfb.scaleViewport = true;
        rfb.background = 'transparent';
        rfb.addEventListener('connect', () => { if (!cancelled) setPhase('connected'); });
        rfb.addEventListener('disconnect', (e) => { if (!cancelled) { setPhase('error'); setStatus(e.detail?.clean ? 'Console disconnessa.' : 'Connessione alla console persa.'); } });
        rfb.addEventListener('securityfailure', () => { if (!cancelled) { setPhase('error'); setStatus('Autenticazione VNC non riuscita.'); } });
        rfbRef.current = rfb;
      } catch (e) {
        if (!cancelled) { setPhase('error'); setStatus('Impossibile avviare la console.'); }
      }
    })();
    return () => { cancelled = true; try { rfbRef.current?.disconnect(); } catch { /* noop */ } rfbRef.current = null; };
  }, [id, attempt]);

  const sendCtrlAltDel = () => { try { rfbRef.current?.sendCtrlAltDel(); } catch { /* noop */ } };
  const reconnect = () => { try { rfbRef.current?.disconnect(); } catch { /* noop */ } setAttempt((a) => a + 1); };
  const fullscreen = () => { const el = modalRef.current; if (!el) return; if (document.fullscreenElement) document.exitFullscreen(); else el.requestFullscreen?.(); };

  const pill = phase === 'connected'
    ? { cls: 'ok', label: 'Connesso' }
    : phase === 'connecting' ? { cls: 'warn', label: 'Connessione…' } : { cls: 'danger', label: 'Non connesso' };

  return (
    <div className="nfx-vnc-scrim" onClick={onClose}>
      <div className="nfx-vnc-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        <div className="nfx-vnc-head">
          <span className="nfx-vnc-head__t"><Terminal size={17} /> Console — {name}</span>
          <div className="nfx-vnc-head__r">
            <span className={`nfx-st nfx-st--${pill.cls}`}>{pill.label}</span>
            <button className="nfx-vnc-x" onClick={onClose} aria-label="Chiudi"><X size={18} /></button>
          </div>
        </div>
        <div className="nfx-vnc-screen" ref={screenRef}>
          {phase !== 'connected' && (
            <div className="nfx-vnc-overlay">
              {phase === 'connecting' && <span className="nfx-vnc-spinner" />}
              <span className="nfx-vnc-msg">{status}</span>
              {phase === 'error' && <button className="nfx-btn-sec nfx-vnc-retry" onClick={reconnect}>Riprova</button>}
            </div>
          )}
        </div>
        <div className="nfx-vnc-foot">
          <div className="nfx-vnc-foot__l">
            <button className="nfx-btn-sec" onClick={sendCtrlAltDel} disabled={phase !== 'connected'}>Ctrl+Alt+Canc</button>
            <button className="nfx-btn-sec" onClick={reconnect}>Riconnetti</button>
            <button className="nfx-btn-sec" onClick={fullscreen}><Maximize2 size={15} style={{ verticalAlign: '-2px', marginRight: 5 }} />Schermo intero</button>
          </div>
          <button className="nfx-btn-pri" onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════ DETTAGLIO VPS — control panel ═══════════════════ */
export const VpsDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const sec = searchParams.get('sec') || 'panel';
  const goSec = (s) => { if (s === 'panel') setSearchParams({}); else setSearchParams({ sec: s }); };
  const [v, setV] = useState(null);
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');
  const [manageOpen, setManageOpen] = useState(false);
  const [dtags, setDtags] = useState([]);
  const [tagModal, setTagModal] = useState(null); // { view, name, color }
  const [opProgress, setOpProgress] = useState(null); // % avanzamento operazione
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState(null);
  const [statoOv, setStatoOv] = useState(null);
  const [fwRules, setFwRules] = useState(null);
  const [fwLoading, setFwLoading] = useState(false);
  const [fwQ, setFwQ] = useState('');
  const [fwBusy, setFwBusy] = useState('');
  const FW_ADD0 = { editId: null, portSel: 'all', apps: [], portType: 'TCP', portSelType: 'select', ports: '', rangeStart: '', rangeStop: '', ipType: 'all', ipSpecificType: 'unique', specificIp: '', specificSubnet: '', ipRangeStart: '', ipRangeStop: '', description: '' };
  const [fwAdd, setFwAdd] = useState(FW_ADD0);
  const [fwAppQ, setFwAppQ] = useState('');
  const [fwResetOpen, setFwResetOpen] = useState(false);
  const [fwMenu, setFwMenu] = useState(null);
  const [fwConfirm, setFwConfirm] = useState(null); // { type: 'delete'|'disable', id }
  const [vncEnabled, setVncEnabled] = useState(false);
  const [showVnc, setShowVnc] = useState(false);
  const [domini, setDomini] = useState(null);
  const [dominiLoading, setDominiLoading] = useState(false);
  const [domQ, setDomQ] = useState('');
  const [domSub, setDomSub] = useState('');
  // Reverse DNS (PTR) per IPv4/IPv6
  const [rdnsV4, setRdnsV4] = useState('');
  const [rdnsV6, setRdnsV6] = useState('');
  const [rdnsLoading, setRdnsLoading] = useState(false);
  const [rdnsBusy, setRdnsBusy] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameVal, setRenameVal] = useState('');
  const [procOpen, setProcOpen] = useState(true);
  const [confirmCfg, setConfirmCfg] = useState(null);
  const [osCat, setOsCat] = useState(null);
  const [riDistro, setRiDistro] = useState('');
  const [riVer, setRiVer] = useState('');
  const [riPwd, setRiPwd] = useState('');
  const [riBusy, setRiBusy] = useState(false);
  const [riConfirm, setRiConfirm] = useState(false);
  const [riConfPwd, setRiConfPwd] = useState('');
  // Disattivazione (termina): step motivo → mail → password
  const [tStep, setTStep] = useState('motivo');
  const [tRating, setTRating] = useState(null);
  const [tReason, setTReason] = useState(null);
  const [tMsg, setTMsg] = useState('');
  const [tPwd, setTPwd] = useState('');
  const [tBusy, setTBusy] = useState(false);
  const [tErr, setTErr] = useState(null);
  const [tEmail, setTEmail] = useState('');
  const termToken = searchParams.get('token');
  // Eliminazione diretta (fatturazione a consumo)
  const [delOpen, setDelOpen] = useState(false);
  const [delPwd, setDelPwd] = useState('');
  const [delAck, setDelAck] = useState(false);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState(null);
  const menuRef = useRef(null);

  const reloadLive = () => api.get(`/api/cloud/vps/${id}/live`).then((r) => setLive(r.data)).catch(() => {});
  const reloadV = () => api.get(`/api/cloud/vps/${id}`).then((r) => setV(r.data)).catch(() => {});
  const persistTags = (tags) => api.put(`/api/cloud/vps/${id}/tags`, { tags }).catch(() => {});
  const openTagModal = () => setTagModal({ view: 'select', name: '', color: TAG_PALETTE[0] });
  const addTag = () => {
    const m = tagModal; if (!m) return;
    const name = (m.name || '').trim(); if (!name) return;
    if (dtags.some((t) => t.name.toLowerCase() === name.toLowerCase())) { setTagModal({ ...m, view: 'select', name: '' }); return; }
    const next = [...dtags, { name, color: m.color }];
    setDtags(next); persistTags(next); setTagModal({ ...m, view: 'select', name: '' });
  };
  const removeTag = (name) => { const next = dtags.filter((t) => t.name !== name); setDtags(next); persistTags(next); };

  const loadFwRules = () => {
    setFwLoading(true);
    return api.get(`/api/cloud/vps/${id}/firewall/rules`)
      .then((r) => setFwRules(r.data?.rules || []))
      .catch(() => setFwRules(null))
      .finally(() => setFwLoading(false));
  };
  useEffect(() => {
    if (sec !== 'firewall') return;
    loadFwRules();
  }, [sec, id]); // eslint-disable-line

  // Submit della pagina "Aggiungere/Modificare una regola" → store per-regola.
  const fwSubmitAdd = async () => {
    const a = fwAdd;
    // 1) porte / protocollo
    let ports = [];
    let proto = 'ALL';
    if (a.portSel === 'all') { flash('err', 'Aprire "tutte le porte" non è consentito: seleziona applicazioni o porte specifiche.'); return; }
    if (a.portSel === 'application') {
      ports = FW_APPS.filter((x) => a.apps.includes(x.name)).flatMap((x) => x.ports.map(String));
      proto = 'ALL';
    } else if (a.portSel === 'manual') {
      proto = a.portType || 'TCP';
      if (a.portType === 'ICMP') {
        ports = []; // ICMP non ha porte
      } else if (a.portSelType === 'range') {
        if (!a.rangeStart.trim() || !a.rangeStop.trim()) { flash('err', 'Indica inizio e fine dell’intervallo di porte.'); return; }
        ports = [`${a.rangeStart.trim()}:${a.rangeStop.trim()}`];
      } else {
        ports = a.ports.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
      }
    }
    if (a.portType !== 'ICMP' && !ports.length) { flash('err', 'Seleziona almeno una porta o un’applicazione.'); return; }
    if (ports.includes('25')) { flash('err', 'Per policy non è possibile aprire la porta 25 (SMTP).'); return; }

    // 2) IP sorgente
    const ips = [];
    if (a.ipType === 'specific') {
      let ip = null;
      if (a.ipSpecificType === 'unique') ip = a.specificIp.trim();
      else if (a.ipSpecificType === 'subnetwork') ip = a.specificIp.trim() && a.specificSubnet.trim() ? `${a.specificIp.trim()}/${a.specificSubnet.trim()}` : a.specificIp.trim();
      else if (a.ipSpecificType === 'range') ip = a.ipRangeStart.trim(); // range non rappresentabile: usiamo l'inizio
      if (!ip) { flash('err', 'Indica l’indirizzo IP sorgente.'); return; }
      ips.push(ip);
    }

    const payload = { proto, ports, ips, description: a.description || '' };
    setFwBusy('add');
    try {
      if (a.editId) {
        await api.put(`/api/cloud/vps/${id}/firewall/rules/${a.editId}`, payload);
        flash('ok', 'Regola modificata.');
      } else {
        await api.post(`/api/cloud/vps/${id}/firewall/rules`, payload);
        flash('ok', 'Regola aggiunta.');
      }
      setFwAdd(FW_ADD0);
      await loadFwRules();
      goSec('firewall');
    } catch (e) {
      flash('err', e.response?.data?.messaggio || 'Operazione firewall non riuscita.');
    } finally { setFwBusy(''); }
  };
  // Precompila la pagina regola per la modifica di una regola esistente.
  const fwEditRule = (r) => {
    const isRange = r.ports.length === 1 && /:/.test(r.ports[0]);
    const ip = (r.ips || [])[0] || '';
    setFwAdd({
      ...FW_ADD0,
      editId: r.id,
      portSel: (r.proto === 'ICMP') ? 'manual' : 'manual',
      portType: r.proto || 'TCP',
      portSelType: isRange ? 'range' : 'select',
      ports: isRange ? '' : r.ports.join(', '),
      rangeStart: isRange ? r.ports[0].split(':')[0] : '',
      rangeStop: isRange ? r.ports[0].split(':')[1] : '',
      ipType: ip ? 'specific' : 'all',
      ipSpecificType: ip.includes('/') ? 'subnetwork' : 'unique',
      specificIp: ip.includes('/') ? ip.split('/')[0] : ip,
      specificSubnet: ip.includes('/') ? ip.split('/')[1] : '',
      description: r.description || '',
    });
    setFwMenu(null);
    goSec('firewall-add');
  };
  const fwDeleteRule = (ruleId) => {
    setFwBusy(String(ruleId));
    return api.delete(`/api/cloud/vps/${id}/firewall/rules/${ruleId}`)
      .then(() => { flash('ok', 'Regola eliminata.'); return loadFwRules(); })
      .catch((e) => flash('err', e.response?.data?.messaggio || 'Eliminazione non riuscita.'))
      .finally(() => setFwBusy(''));
  };
  const fwToggleRule = (ruleId, enabled) => {
    setFwBusy(String(ruleId));
    return api.post(`/api/cloud/vps/${id}/firewall/rules/${ruleId}/toggle`, { enabled })
      .then(() => { flash('ok', enabled ? 'Regola attivata.' : 'Regola disattivata.'); return loadFwRules(); })
      .catch((e) => flash('err', e.response?.data?.messaggio || 'Operazione non riuscita.'))
      .finally(() => setFwBusy(''));
  };
  // Conferma azione da menu ⋮ (disattiva/elimina), poi esegue.
  const fwConfirmRun = () => {
    if (!fwConfirm) return;
    const { type, id: rid } = fwConfirm;
    const p = type === 'delete' ? fwDeleteRule(rid) : fwToggleRule(rid, false);
    (p || Promise.resolve()).finally(() => setFwConfirm(null));
  };
  const fwReset = () => {
    setFwBusy('reset');
    api.post(`/api/cloud/vps/${id}/firewall`, { op: 'reset_default' })
      .then(() => { flash('ok', 'Regole di origine ripristinate.'); setFwResetOpen(false); return loadFwRules(); })
      .catch((e) => flash('err', e.response?.data?.messaggio || 'Ripristino non riuscito.'))
      .finally(() => setFwBusy(''));
  };

  useEffect(() => {
    if (sec !== 'domains') return;
    setDominiLoading(true);
    api.get(`/api/cloud/vps/${id}/domini`).then((r) => setDomini(r.data)).catch(() => setDomini(null)).finally(() => setDominiLoading(false));
  }, [sec, id]);

  useEffect(() => {
    if (sec !== 'reverse-dns') return;
    setRdnsLoading(true);
    api.get(`/api/cloud/vps/${id}/reverse-dns`)
      .then((r) => { const d = r.data || {}; setRdnsV4(d.ipv4_host || ''); setRdnsV6(d.ipv6_host || ''); })
      .catch(() => { setRdnsV4(''); setRdnsV6(''); })
      .finally(() => setRdnsLoading(false));
  }, [sec, id]);

  const saveReverseDns = () => {
    setRdnsBusy(true);
    api.post(`/api/cloud/vps/${id}/reverse-dns`, { ipv4_host: rdnsV4.trim(), ipv6_host: rdnsV6.trim() })
      .then((r) => {
        const d = r.data || {};
        if (d.dns === false) flash('err', d.messaggio || 'PTR non applicato sui nameserver.');
        else flash('ok', 'Reverse DNS registrato.');
      })
      .catch((e) => flash('err', e.response?.data?.messaggio || 'Impossibile registrare il reverse DNS.'))
      .finally(() => setRdnsBusy(false));
  };

  useEffect(() => {
    if (sec !== 'reinstall' || osCat) return;
    api.get('/api/cloud/os-catalog').then((r) => setOsCat(Array.isArray(r.data) ? r.data : [])).catch(() => setOsCat([]));
  }, [sec, osCat]);

  // Preseleziona la prima distro/versione quando il catalogo è pronto
  useEffect(() => {
    if (sec !== 'reinstall' || !osCat || !osCat.length || riDistro) return;
    const d = osCat[0];
    setRiDistro(d.slug);
    if (d.versioni && d.versioni[0]) setRiVer(String(d.versioni[0].id));
  }, [sec, osCat, riDistro]);

  // Arrivo dall'email di verifica (token in URL) → step password
  useEffect(() => {
    if (sec !== 'termina' || !termToken) return;
    setTStep('verifica-token');
    api.get(`/api/cloud/vps/${id}/termina/verify?token=${encodeURIComponent(termToken)}`)
      .then(() => setTStep('password'))
      .catch((e) => { setTStep('password'); setTErr(e?.response?.data?.messaggio || 'Link non valido o scaduto'); });
  }, [sec, termToken, id]);

  useEffect(() => {
    let alive = true; setLoading(true);
    api.get(`/api/cloud/vps/${id}`)
      .then((r) => { if (alive) { setV(r.data); const t = parseMeta(r.data?.metadata).tags; setDtags(Array.isArray(t) ? t : []); } })
      .catch(() => { if (alive) setV(null); })
      .finally(() => { if (alive) setLoading(false); });
    api.get(`/api/cloud/vps/${id}/live`).then((r) => { if (alive) setLive(r.data); }).catch(() => {});
    api.get(`/api/cloud/vps/${id}/vnc`).then((r) => { if (alive) setVncEnabled(!!r.data?.enabled); }).catch(() => {});
    return () => { alive = false; };
  }, [id]);

  useEffect(() => {
    if (!manageOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setManageOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [manageOpen]);

  // Operazione in corso (reinstall / upgrade): polling del progresso reale da Virtualizor (tetto 12 min di fallback)
  const opIniziata = (() => { try { return parseMeta(v?.metadata)?.operazione?.iniziata || null; } catch { return null; } })();
  const opTipo = (() => { try { return parseMeta(v?.metadata)?.operazione?.tipo || 'reinstall'; } catch { return 'reinstall'; } })();
  useEffect(() => {
    if (!opIniziata) { setOpProgress(null); return undefined; }
    if (Date.now() - new Date(opIniziata).getTime() > 12 * 60 * 1000) {
      api.post(`/api/cloud/vps/${id}/operazione/clear`).catch(() => {}).finally(reloadV);
      return undefined;
    }
    let stop = false;
    const poll = () => {
      api.get(`/api/cloud/vps/${id}/task-progress`).then((r) => {
        if (stop) return;
        const d = r.data || {};
        if (d.found && typeof d.progress === 'number') setOpProgress(d.progress);
        if (d.found && (d.done || d.errored)) {
          api.post(`/api/cloud/vps/${id}/operazione/clear`).catch(() => {}).finally(reloadV);
        }
      }).catch(() => {});
      reloadLive();
    };
    poll();
    const t = setInterval(poll, 5000);
    return () => { stop = true; clearInterval(t); };
    // eslint-disable-line
  }, [opIniziata, id]);

  if (loading) return <div className="nfx-card" style={{ marginTop: 8 }}><div className="nfx-loading">Caricamento…</div></div>;
  if (!v || v.errore) return <div className="nfx-card nfx-card--pad" style={{ marginTop: 8 }}>VPS non trovata.</div>;

  const base = vpsInfo(v);
  // Merge con i dati live di Virtualizor (prevalgono)
  const liveIps = live?.ips || [];
  const info = {
    ...base,
    os: base.os !== '—' ? base.os : prettyOs(live?.os),
    ipv4: liveIps.find((x) => !String(x).includes(':')) || base.ipv4,
    ipv6: liveIps.find((x) => String(x).includes(':')) || base.ipv6,
    cpu: live?.cpu ? `${live.cpu} vCPU` : base.cpu,
    ram: live?.ram_mb ? `${Math.round(live.ram_mb / 1024)} GB` : base.ram,
    disk: live?.disk_gb ? `${live.disk_gb} GB` : base.disk,
    vncPort: live?.vnc_port || null,
  };
  const bwUsed = live?.used_bandwidth_gb != null ? live.used_bandwidth_gb : null;
  const bwTotal = live?.bandwidth_gb || 0;
  // Traffico e banda dai dati reali di Virtualizor
  const trafico = bwTotal > 0
    ? `${(bwUsed ?? 0).toFixed(bwUsed && bwUsed < 10 ? 2 : 0)} / ${bwTotal} GB`
    : 'Illimitato';
  const netKbps = live?.network_speed_kbps || 0;
  const banda = netKbps > 0 ? `${Math.round((netKbps * 8) / 1000)} Mb/s` : 'Illimitato';
  const liveStato = live ? (live.suspended ? 'sospeso' : (live.power_status === 1 ? 'avviato' : 'fermo')) : null;
  const stato = statoOv !== null ? statoOv : (liveStato || v.stato || '');
  const st = STATE_TAG(stato);
  const running = st.cls === 'ok';
  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); };
  const offer = () => window.open('https://neoflux.it', '_blank');
  // Upgrade/cambio offerta: porta allo shop (sessione condivisa via cookie .neoflux.it).
  const goUpgrade = () => { window.location.href = `https://shop.neoflux.it/order2/vps/${v.numero ?? id}`; };
  const copy = (text) => { try { navigator.clipboard?.writeText(text); flash('ok', 'Copiato negli appunti.'); } catch { /* no-op */ } };
  const dm = dMinus(v.data_scadenza);
  const opInProgress = !!opIniziata && (Date.now() - new Date(opIniziata).getTime() < 12 * 60 * 1000);

  const azione = (az, label) => {
    if (busy) return; setBusy(az); setManageOpen(false);
    api.post(`/api/cloud/vps/${id}/azione`, { azione: az })
      .then(() => {
        setStatoOv(null);
        flash('ok', `${label}: richiesta inviata al server.`);
        setTimeout(reloadLive, 3500);
      })
      .catch((e) => flash('err', e?.response?.data?.messaggio || 'Operazione non riuscita'))
      .finally(() => setBusy(''));
  };

  // Registry azioni con relativa modale di conferma (testi come da riferimento)
  const ACTIONS = {
    shutdown: { az: 'shutdown', label: 'Arresto', title: 'Arrestare il server', lines: ['È sicuro di voler chiudere questo Cloud?'], btn: 'Convalidare', danger: false },
    poweron: { az: 'poweron', label: 'Avvio', title: 'Avviare il server', lines: ['Desideri veramente avviare questo server cloud?'], btn: 'Convalidare', danger: false },
    reboot: { az: 'reboot', label: 'Riavvio (Classic)', title: 'Riavviare il server (Modalità classica)', lines: ['La modalità classica consente di riavviare normalmente il tuo server.', 'Desideri veramente riavviare questo server cloud?'], btn: 'Riavviare', danger: true },
    safe: { az: 'reset', label: 'Riavvio (Safe)', title: 'Riavviare il server (Modalità sicura)', lines: ['La modalità sicura consente di diagnosticare e risolvere eventuali problemi da un sistema operativo temporaneo.', 'Desideri veramente riavviare questo server cloud in modalità sicura?'], btn: 'Riavviare', danger: true },
    reset: { az: 'reset', label: 'Reset', title: 'Ripristinare il server', lines: ['Desideri veramente ripristinare questo server cloud?'], btn: 'Convalidare', danger: true },
  };
  const ask = (key) => { setManageOpen(false); setConfirmCfg(ACTIONS[key]); };
  const confirmRun = () => {
    if (!confirmCfg) return;
    const { az, label } = confirmCfg;
    setConfirmCfg(null);
    azione(az, label);
  };

  const NAV_LABEL = { panel: 'Pannello di controllo', domains: 'Domini associati', firewall: 'Firewall', snapshot: 'Snapshot', maintenance: 'Manutenzioni programmate' };

  const saveRename = () => {
    const nome = renameVal.trim();
    if (!nome) return;
    setBusy('rename');
    api.patch(`/api/cloud/vps/${id}`, { nome_servizio: nome })
      .then(() => { setV((prev) => ({ ...prev, nome_servizio: nome })); setRenameOpen(false); flash('ok', 'Nome aggiornato.'); })
      .catch(() => flash('err', 'Impossibile aggiornare il nome.'))
      .finally(() => setBusy(''));
  };
  const assistenza = () => { setManageOpen(false); navigate(V2_BASE + '/support'); };

  const distroList = osCat || [];
  const riCurDistro = distroList.find((d) => d.slug === riDistro) || null;
  const riVersioni = riCurDistro?.versioni || [];
  const pickDistro = (slug) => { setRiDistro(slug); const d = distroList.find((x) => x.slug === slug); setRiVer(d?.versioni?.[0] ? String(d.versioni[0].id) : ''); };

  // Step 1: dalla form apre la modale di conferma
  const askReinstall = () => {
    if (!riVer) return flash('err', 'Seleziona una versione.');
    if (riPwd.length < 8) return flash('err', 'La password di root deve avere almeno 8 caratteri.');
    setRiConfPwd('');
    setRiConfirm(true);
  };
  // Step 2: conferma → esegue davvero
  const doReinstall = () => {
    if (riBusy) return;
    setRiBusy(true);
    api.post(`/api/cloud/vps/${id}/reinstall`, { os_version_id: Number(riVer), password: riPwd, access_password: riConfPwd })
      .then(() => { setRiConfirm(false); setRiPwd(''); reloadV().then(() => goSec('panel')); })
      .catch((e) => flash('err', e?.response?.data?.messaggio || 'Reinstallazione non riuscita'))
      .finally(() => setRiBusy(false));
  };

  const aConsumo = v.modalita_fatturazione === 'consumo';
  const openTermina = () => {
    setManageOpen(false);
    if (aConsumo) { setDelPwd(''); setDelAck(false); setDelErr(null); setDelOpen(true); }
    else { setTStep('motivo'); setTRating(null); setTReason(null); setTMsg(''); setTErr(null); goSec('termina'); }
  };
  const eliminaConsumo = () => {
    if (!delPwd || !delAck) return;
    setDelBusy(true); setDelErr(null);
    api.post(`/api/cloud/vps/${id}/elimina`, { password: delPwd })
      .then(() => { flash('ok', 'Prodotto eliminato.'); setDelOpen(false); setTimeout(() => navigate(V2_BASE + '/cloud-dedicated/vps'), 1200); })
      .catch((e) => { setDelErr(e?.response?.data?.messaggio || 'Eliminazione non riuscita'); setDelBusy(false); });
  };

  const REASONS = [
    { v: 0, t: 'Difficile da usare', ph: 'Quali problemi hai riscontrato con il prodotto?' },
    { v: 1, t: 'Mancanza di funzionalità', ph: 'Di quali funzionalità hai bisogno?' },
    { v: 2, t: 'Non soddisfa le mie esigenze', ph: 'Quali erano le tue esigenze / i tuoi obiettivi nell’utilizzo del prodotto?' },
    { v: 3, t: 'Passo alla concorrenza', ph: 'Per quale concorrente ci lasci?' },
    { v: 4, t: 'Non sono soddisfatta/o del servizio clienti', ph: 'Qual è stata la tua esperienza con il nostro servizio clienti?' },
    { v: 5, t: 'Prezzo troppo alto', ph: 'Hai optato per un’alternativa più economica?' },
    { v: 6, t: 'Non ho più bisogno del prodotto', ph: 'Cosa potremmo fare per migliorare il prodotto in futuro?' },
    { v: 7, t: 'Altro', ph: 'Hai riscontrato un altro problema?' },
  ];
  const termInviaRichiesta = () => {
    if (tRating == null || tReason == null) return;
    setTBusy(true); setTErr(null);
    api.post(`/api/cloud/vps/${id}/termina/richiesta`, { rating: tRating, reason: REASONS[tReason]?.t, message: tMsg })
      .then((r) => { setTEmail(r.data?.email || ''); setTStep('mail'); })
      .catch((e) => setTErr(e?.response?.data?.messaggio || 'Impossibile inviare la richiesta'))
      .finally(() => setTBusy(false));
  };
  const termConferma = () => {
    if (!tPwd) return;
    setTBusy(true); setTErr(null);
    api.post(`/api/cloud/vps/${id}/termina/conferma`, { token: termToken, password: tPwd })
      .then(() => { setTBusy(false); setTStep('success'); })
      .catch((e) => { setTErr(e?.response?.data?.messaggio || 'Disattivazione non riuscita'); setTBusy(false); });
  };

  const QuickLink = ({ label, onClick, disabled }) => (
    <button className="nfx-vpsd-qa-link" onClick={onClick} disabled={disabled}>
      <ChevronRight size={16} className="nfx-vpsd-qa-arrow" /> <span>{label}</span>
    </button>
  );

  return (
    <div>
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE + '/cloud-dedicated/vps')} style={{ fontSize: 13.5, width: 'auto' }}>VPS</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        {sec === 'reinstall' ? (
          <>
            <button className="nfx-crumb__home" onClick={() => goSec('panel')} style={{ fontSize: 13.5, width: 'auto' }}>{v.nome_servizio}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <span className="nfx-crumb__cur">Ripristinare il server</span>
          </>
        ) : sec === 'termina' ? (
          <>
            <button className="nfx-crumb__home" onClick={() => goSec('panel')} style={{ fontSize: 13.5, width: 'auto' }}>{v.nome_servizio}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <span className="nfx-crumb__cur">Disattivazione</span>
          </>
        ) : sec === 'firewall-add' ? (
          <>
            <button className="nfx-crumb__home" onClick={() => goSec('panel')} style={{ fontSize: 13.5, width: 'auto' }}>{v.nome_servizio}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <button className="nfx-crumb__home" onClick={() => goSec('firewall')} style={{ fontSize: 13.5, width: 'auto' }}>Firewall</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <span className="nfx-crumb__cur">{fwAdd.editId ? 'Modificare una regola' : 'Aggiungere una regola'}</span>
          </>
        ) : sec === 'firewall' ? (
          <>
            <button className="nfx-crumb__home" onClick={() => goSec('panel')} style={{ fontSize: 13.5, width: 'auto' }}>{v.nome_servizio}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <span className="nfx-crumb__cur">Firewall</span>
          </>
        ) : sec === 'domains' ? (
          <>
            <button className="nfx-crumb__home" onClick={() => goSec('panel')} style={{ fontSize: 13.5, width: 'auto' }}>{v.nome_servizio}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <span className="nfx-crumb__cur">Domini associati</span>
          </>
        ) : sec === 'domains-add' ? (
          <>
            <button className="nfx-crumb__home" onClick={() => goSec('panel')} style={{ fontSize: 13.5, width: 'auto' }}>{v.nome_servizio}</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <button className="nfx-crumb__home" onClick={() => goSec('domains')} style={{ fontSize: 13.5, width: 'auto' }}>Domini associati</button>
            <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
            <span className="nfx-crumb__cur">Associare un dominio</span>
          </>
        ) : (
          <span className="nfx-crumb__cur">{v.nome_servizio}</span>
        )}
      </div>

      {msg && <div className={`nfx-dom-flash nfx-dom-flash--${msg.type}`} style={{ marginBottom: 18 }}>{msg.text}</div>}

      {showVnc && <VncConsole id={id} name={v.nome_servizio} onClose={() => setShowVnc(false)} />}

      {delOpen && (
        <div className="nfx-cfm-scrim" onClick={() => setDelOpen(false)}>
          <div className="nfx-cfm" onClick={(e) => e.stopPropagation()}>
            <button className="nfx-cfm__close" onClick={() => setDelOpen(false)} aria-label="Chiudi"><X size={13} /></button>
            <div className="nfx-cfm__body">
              <div className="nfx-del-illu"><span className="nfx-del-illu__ic"><X size={26} /></span></div>
              <div className="nfx-del-title">Stai per eliminare il prodotto <strong>{v.nome_servizio}</strong></div>
              <ul className="nfx-del-list">
                <li>L&rsquo;eliminazione del prodotto <strong>sarà immediata e definitiva.</strong></li>
                <li>Le fatture aperte e fuori forfait <strong>dovranno essere regolate.</strong></li>
              </ul>
              <p className="nfx-del-p">Per convalidare l&rsquo;eliminazione, inserisci la password di accesso al tuo account.</p>
              <input type="password" className="nfx-matsel__sel nfx-ri-input" style={{ maxWidth: 376 }} placeholder="Password di accesso"
                value={delPwd} onChange={(e) => setDelPwd(e.target.value)} autoComplete="new-password" />
              <label className="nfx-del-ack">
                <input type="checkbox" checked={delAck} onChange={(e) => setDelAck(e.target.checked)} />
                <span><strong>Ho letto questi avvisi</strong> e desidero comunque procedere con la cancellazione.</span>
              </label>
              {delErr && <div className="nfx-carderr" style={{ marginTop: 14 }}>{delErr}</div>}
              <div className="nfx-cfm__btns">
                <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => setDelOpen(false)}>Annullare</button>
                <button className="nfx-cfm-btn nfx-cfm-btn--danger" onClick={eliminaConsumo} disabled={delBusy || !delPwd || !delAck}>{delBusy ? 'Attendere…' : 'Eliminare'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {riConfirm && (
        <div className="nfx-cfm-scrim" onClick={() => setRiConfirm(false)}>
          <div className="nfx-cfm" onClick={(e) => e.stopPropagation()}>
            <button className="nfx-cfm__close" onClick={() => setRiConfirm(false)} aria-label="Chiudi"><X size={13} /></button>
            <div className="nfx-cfm__body">
              <p className="nfx-cfm__p">Attenzione! Stai per resettare il tuo server. Durante il processo, tutti i contenuti del volume dedicato al sistema operativo (/dev/sda, /dev/sda1 o /dev/vda) verranno eliminati definitivamente.</p>
              <p className="nfx-cfm__p">Desidera veramente reinstallare questo server Cloud?</p>
              <div className="nfx-ri-group" style={{ margin: '22px 0 0' }}>
                <label className="nfx-ri-label" htmlFor="ri-access-pwd">Per sicurezza, inserisca la sua password di accesso *</label>
                <input id="ri-access-pwd" type="password" className="nfx-matsel__sel nfx-ri-input" value={riConfPwd}
                  onChange={(e) => setRiConfPwd(e.target.value)} autoComplete="off" autoFocus />
              </div>
              <div className="nfx-cfm__btns">
                <button className="nfx-cfm-btn nfx-cfm-btn--danger" onClick={doReinstall} disabled={riBusy || riConfPwd.length < 1}>{riBusy ? 'Attendere…' : 'Re-inizializzare'}</button>
                <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => setRiConfirm(false)}>Annullare</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmCfg && (
        <div className="nfx-cfm-scrim" onClick={() => setConfirmCfg(null)}>
          <div className="nfx-cfm" onClick={(e) => e.stopPropagation()}>
            <button className="nfx-cfm__close" onClick={() => setConfirmCfg(null)} aria-label="Chiudi"><X size={13} /></button>
            <div className="nfx-cfm__body">
              {confirmCfg.lines.map((l, i) => <p key={i} className="nfx-cfm__p">{l}</p>)}
              <div className="nfx-cfm__btns">
                <button className={`nfx-cfm-btn ${confirmCfg.danger ? 'nfx-cfm-btn--danger' : 'nfx-cfm-btn--primary'}`} onClick={confirmRun} disabled={!!busy}>{confirmCfg.btn}</button>
                <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => setConfirmCfg(null)}>Annullare</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {renameOpen && (
        <div className="nfx-vnc-scrim" onClick={() => setRenameOpen(false)}>
          <div className="nfx-hmodal" onClick={(e) => e.stopPropagation()}>
            <div className="nfx-hmodal__title">Cambia dettagli</div>
            <p className="nfx-dom-note" style={{ marginBottom: 12 }}>Nome visualizzato della VPS nel tuo spazio cliente.</p>
            <input className="nfx-dom-nsinput" style={{ width: '100%', boxSizing: 'border-box' }} value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)} placeholder="Nome della VPS" autoFocus />
            <div className="nfx-dom-nsbtns" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="nfx-btn-sec" onClick={() => setRenameOpen(false)} disabled={busy === 'rename'}>Annulla</button>
              <button className="nfx-btn-pri" onClick={saveRename} disabled={busy === 'rename'}>{busy === 'rename' ? 'Salvataggio…' : 'Salva'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ Operazione in corso (reinstall) ══════════ */}
      {opInProgress && (
        <div className="nfx-card nfx-card--pad">
          <div className="nfx-opip">
            <span className="nfx-opip__ic"><RefreshCw size={44} /></span>
            <h2 className="nfx-opip__title">{opTipo === 'upgrade'
              ? 'Si prega di attendere, aggiornamento dell’offerta in corso...'
              : 'Si prega di attendere, reimpostazione del server in corso...'}</h2>
            <p className="nfx-opip__text">{opTipo === 'upgrade'
              ? 'Stiamo applicando la nuova configurazione (CPU/RAM/disco). Il server si riavvia, riprova tra qualche minuto.'
              : 'Riprova tra qualche minuto.'}</p>
            {opProgress != null && (
              <div className="nfx-opip__prog">
                <div className="nfx-opip__bar"><span style={{ width: `${opProgress}%` }} /></div>
                <div className="nfx-opip__pct">{opProgress}%</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ══════════ Pannello di controllo ══════════ */}
      {sec === 'panel' && !opInProgress && (
        <div className="nfx-vps-grid">
          {/* Colonna sinistra: hero + info/users */}
          <div className="nfx-card" style={{ overflow: 'hidden' }}>
            <div className="nfx-vps-hero">
              <div className="nfx-vps-hero__actions" ref={menuRef}>
                <button className="nfx-vps-gestbtn" onClick={() => setManageOpen((o) => !o)}>Gestire <span className="nfx-vps-caret" /></button>
                {manageOpen && (
                  <div className="nfx-vps-gestmenu">
                    <button onClick={() => { setManageOpen(false); setRenameVal(v.nome_servizio || ''); setRenameOpen(true); }}>Modificare le informazioni</button>
                    <button onClick={assistenza}>Modificare l&rsquo;offerta</button>
                    <button onClick={() => { setManageOpen(false); navigate(V2_BASE + '/billing/rinnovo'); }}>Rinnovare</button>
                    {running
                      ? <button onClick={() => ask('shutdown')}>Arrestare il server</button>
                      : <button onClick={() => ask('poweron')}>Avviare il server</button>}
                    <button onClick={() => ask('reboot')}>Riavviare il server (Modalità classica)</button>
                    <button onClick={() => ask('safe')}>Riavviare il server (Modalità sicura)</button>
                    <button onClick={() => { setManageOpen(false); goSec('reinstall'); }}>Ripristinare il server</button>
                    <button onClick={assistenza}>Trasferire a un altro account</button>
                    <div className="nfx-vps-gestmenu__sep" />
                    <button onClick={openTermina}>Rescindere</button>
                  </div>
                )}
              </div>
              <div className="nfx-vps-hero__ic"><Server size={30} /></div>
              <div className="nfx-vps-hero__name">{v.nome_servizio}</div>
              <div className="nfx-vps-hero__host">Nome del host : {info.hostname}</div>
            </div>

            <div className="nfx-card--pad">
              <div className="nfx-vps-tabs">
                <button className={`nfx-vps-tab${tab === 'info' ? ' is-active' : ''}`} onClick={() => setTab('info')}>INFORMAZIONI</button>
                <button className={`nfx-vps-tab${tab === 'users' ? ' is-active' : ''}`} onClick={() => setTab('users')}>UTENTI</button>
              </div>
              {tab === 'info' ? (
                <div className="nfx-vpsd-info">
                  <div className="nfx-vpsd-field">
                    <span className="nfx-vpsd-label">Etichetta</span>
                    <div className="nfx-vps-tags">
                      <button className="nfx-vps-tagadd" aria-label="Aggiungi un tag" title="Gestisci i tag" onClick={openTagModal}><PlusCircle size={16} /></button>
                      {dtags.map((t, ti) => (
                        <span key={ti} className="nfx-vps-tagchip" style={{ background: t.color }}>{t.name}</span>
                      ))}
                    </div>
                  </div>

                  <div className="nfx-vpsd-field">
                    <span className="nfx-vpsd-label">Statuto</span>
                    <span className={`nfx-vpsd-status nfx-vpsd-status--${st.cls}`}>{running ? 'AVVIA' : st.label.toUpperCase()}</span>
                  </div>

                  <div className="nfx-vpsd-row">
                    <div className="nfx-vpsd-field">
                      <span className="nfx-vpsd-label">Offerta</span>
                      <span className="nfx-vpsd-value">{info.plan}</span>
                    </div>
                    <div className="nfx-vpsd-field">
                      <span className="nfx-vpsd-label">Scadenza</span>
                      <span className="nfx-vpsd-value">{fmtDate(v.data_scadenza)}{dm != null && dm >= 0 ? ` (G-${dm})` : ''}</span>
                    </div>
                  </div>

                  <div className="nfx-vpsd-field">
                    <span className="nfx-vpsd-label">IPv4</span>
                    <span className="nfx-vpsd-value">{info.ipv4}</span>
                    <button className="nfx-acc-link" onClick={() => goSec('reverse-dns')}>Configurare il reverse DNS</button>
                  </div>

                  <div className="nfx-vpsd-field">
                    <span className="nfx-vpsd-label">IPv6</span>
                    <span className="nfx-vpsd-value">{info.ipv6}</span>
                    <button className="nfx-acc-link" onClick={() => goSec('reverse-dns')}>Configurare il reverse DNS</button>
                  </div>

                  <div className="nfx-vpsd-config">
                    <span>{[info.cpu, info.ram && `${info.ram} RAM`, info.disk].filter(Boolean).join(', ') || '—'}</span>
                    <button className="nfx-acc-link" onClick={offer}>Modificare</button>
                  </div>

                  <div className="nfx-vpsd-row">
                    <div className="nfx-vpsd-field">
                      <span className="nfx-vpsd-label">Traffico</span>
                      <span className="nfx-vpsd-value">{trafico}</span>
                    </div>
                    <div className="nfx-vpsd-field">
                      <span className="nfx-vpsd-label">Banda</span>
                      <span className="nfx-vpsd-value">{banda}</span>
                    </div>
                  </div>

                  <div className="nfx-vpsd-row">
                    <div className="nfx-vpsd-field">
                      <span className="nfx-vpsd-label">Posizione</span>
                      <span className="nfx-vpsd-value">{info.datacenter || 'ITROM1'}</span>
                    </div>
                    <div className="nfx-vpsd-field">
                      <span className="nfx-vpsd-label">Messa in servizio</span>
                      <span className="nfx-vpsd-value">{v.data_attivazione ? fmtDate(v.data_attivazione) : '—'}</span>
                    </div>
                  </div>

                  <div className="nfx-vpsd-field">
                    <span className="nfx-vpsd-label">Distribuzione / versione</span>
                    <span className="nfx-vpsd-value">{info.os}</span>
                  </div>
                </div>
              ) : (
                <div className="nfx-vpsd-info">
                  <div className="nfx-vpsd-field">
                    <span className="nfx-vpsd-label">Utente SSH</span>
                    <span className="nfx-vpsd-value nfx-cell-mono">{info.ssh}</span>
                  </div>
                  <div className="nfx-vpsd-field">
                    <span className="nfx-vpsd-label">Password root</span>
                    <span className="nfx-vpsd-value nfx-cell-mono">{info.rootpw ? info.rootpw : '••••••••'}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Colonna destra: azioni rapide + procedura di avvio + domini */}
          <div>
            <div className="nfx-card nfx-card--pad" style={{ marginBottom: 20 }}>
              <div className="nfx-vps-qa__head">
                <div className="nfx-hdd-cardtitle" style={{ margin: 0 }}>Azioni rapide</div>
                <button className="nfx-vps-upgrade" onClick={() => setUpgradeOpen(true)}>Fai evolvere la tua offerta</button>
              </div>
              <div className="nfx-vpsd-qa">
                <div className="nfx-vpsd-qa-col">
                  {running
                    ? <QuickLink label="Arrestare il server" onClick={() => ask('shutdown')} disabled={!!busy} />
                    : <QuickLink label="Avviare il server" onClick={() => ask('poweron')} disabled={!!busy} />}
                  <QuickLink label="Riavviare il server (Modalità classica)" onClick={() => ask('reboot')} disabled={!!busy} />
                  <QuickLink label="Riavviare il server (Modalità sicura)" onClick={() => ask('safe')} disabled={!!busy} />
                  <QuickLink label="Ripristinare il server" onClick={() => goSec('reinstall')} disabled={!!busy} />
                  {vncEnabled && <QuickLink label="Aprire la console VNC" onClick={() => setShowVnc(true)} />}
                </div>
                <div className="nfx-vpsd-qa-col">
                  <QuickLink label="Impostare il firewall" onClick={() => goSec('firewall')} />
                  <QuickLink label="Gestire gli snapshot" onClick={() => goSec('snapshot')} />
                  <QuickLink label="Fessure di manutenzione" onClick={() => goSec('maintenance')} />
                  <QuickLink label="Configurare il reverse DNS" onClick={() => goSec('reverse-dns')} />
                </div>
              </div>
            </div>

            <div className="nfx-card nfx-card--pad" style={{ marginBottom: 20 }}>
              <div className="nfx-hdd-cardtitle">Procedura di avvio</div>
              <button className="nfx-vpsd-collapse" onClick={() => setProcOpen((o) => !o)}>
                {procOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                <span>Per rendere operativo il tuo VPS segui i seguenti passaggi:</span>
              </button>
              {procOpen && (
                <ol className="nfx-vpsd-steps">
                  <li><span className="nfx-vpsd-step-n">1</span><span>Controlla che la porta SSH sia aperta nel <button className="nfx-acc-link" onClick={() => goSec('firewall')}>firewall</button></span></li>
                  <li><span className="nfx-vpsd-step-n">2</span><span>Apri un terminale/console</span></li>
                  <li><span className="nfx-vpsd-step-n">3</span><span>Accedi a SSH con i dati di accesso seguenti</span></li>
                </ol>
              )}
              <hr className="nfx-vpsd-hr" />
              <div className="nfx-vpsd-conn">
                <div className="nfx-vpsd-conn__item nfx-vpsd-conn__item--wide">
                  <span className="nfx-vpsd-label">Connessione SSH</span>
                  <div className="nfx-vpsd-copyline"><span className="nfx-cell-mono">ssh {info.ssh}@{info.ipv4}</span>
                    <button className="nfx-vpsd-copy" onClick={() => copy(`ssh ${info.ssh}@${info.ipv4}`)}><Copy size={15} /></button></div>
                </div>
                <div className="nfx-vpsd-conn__item">
                  <span className="nfx-vpsd-label">IPv4</span>
                  <div className="nfx-vpsd-copyline"><span className="nfx-cell-mono">{info.ipv4}</span>
                    <button className="nfx-vpsd-copy" onClick={() => copy(info.ipv4)}><Copy size={15} /></button></div>
                </div>
                <div className="nfx-vpsd-conn__item">
                  <span className="nfx-vpsd-label">IPv6</span>
                  <div className="nfx-vpsd-copyline"><span className="nfx-cell-mono nfx-vpsd-ellip">{info.ipv6}</span>
                    <button className="nfx-vpsd-copy" onClick={() => copy(info.ipv6)}><Copy size={15} /></button></div>
                </div>
              </div>
            </div>

            <div className="nfx-card nfx-card--pad">
              <div className="nfx-cp-empty" style={{ border: 'none' }}>
                <span className="nfx-vpsd-emptyic"><span className="nfx-vpsd-emptyimg" role="img" aria-label="Domini" /></span>
                <p className="nfx-vpsd-emptytitle">Nessun dominio associato per il momento</p>
                <button className="nfx-cfm-btn nfx-cfm-btn--primary" onClick={() => goSec('domains')} style={{ marginTop: 12 }}>Associare un dominio</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════ Firewall ══════════ */}
      {sec === 'firewall' && !opInProgress && (() => {
        const rules = Array.isArray(fwRules) ? fwRules.map((r) => {
          const isIcmp = r.proto === 'ICMP';
          const porta = isIcmp ? '—' : (r.ports && r.ports.length ? r.ports.join(', ') : 'Tutti');
          const host = r.ips && r.ips.length ? r.ips.join(', ') : 'Tutte';
          const desc = r.description || FW_PORT_NAME[(r.ports || [])[0]] || (isIcmp ? 'ICMP' : '');
          const tipo = (r.proto && r.proto !== 'ALL') ? r.proto : 'Tutti';
          return { ...r, tipo, porta, host, desc };
        }) : [];
        const q = fwQ.trim().toLowerCase();
        const shown = q ? rules.filter((r) => [r.tipo, r.porta, r.host, r.desc].join(' ').toLowerCase().includes(q)) : rules;
        const exportCsv = () => {
          const head = ['Tipo', 'Porta', 'IP/Ospite', 'Statuto', 'Descrizione'];
          const rowsCsv = rules.map((r) => [r.tipo, r.porta, r.host, r.enabled ? 'Attivato' : 'Disattivato', r.desc]);
          const csv = [head, ...rowsCsv].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
          const blob = new Blob([csv], { type: 'text/csv' });
          const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
          a.download = `firewall-${v.nome_servizio || id}.csv`; a.click(); URL.revokeObjectURL(a.href);
        };
        return (
          <>
            <div style={{ background: 'var(--nfx-sector-soft, #fff7e6)', border: '1px solid var(--nfx-warning, #d98a00)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.5, lineHeight: 1.5 }}>
              <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1.3 }}>⚠️</span>
              <div>
                <b>Gestione firewall in aggiornamento.</b> Stiamo aggiornando l'integrazione del firewall VPS dopo un cambiamento lato infrastruttura di virtualizzazione. Le modifiche effettuate da questa pagina potrebbero non essere applicate immediatamente al VPS. Per aperture o chiusure di porte urgenti, contatta il supporto NeoFlux.
              </div>
            </div>
            {fwLoading && !fwRules ? (
              <div className="nfx-card nfx-card--pad"><div className="nfx-loading">Caricamento regole…</div></div>
            ) : !fwRules ? (
              <div className="nfx-card nfx-card--pad"><div className="nfx-loading">Impossibile leggere il firewall.</div></div>
            ) : rules.length === 0 ? (
              <div className="nfx-card nfx-card--pad">
                <div className="nfx-cp-empty" style={{ border: 'none' }}>
                  <Shield size={40} />
                  <p><b>Nessuna regola definita per il momento</b></p>
                  <button className="nfx-cfm-btn nfx-cfm-btn--primary" style={{ marginTop: 8 }} disabled={!!fwBusy} onClick={() => { setFwAdd(FW_ADD0); goSec('firewall-add'); }}>Aggiungere una regola</button>
                </div>
              </div>
            ) : (
              <div className="nfx-card">
                <div className="nfx-tb nfx-fwtb">
                  <button className="nfx-cfm-btn nfx-cfm-btn--primary" disabled={!!fwBusy} onClick={() => { setFwAdd(FW_ADD0); goSec('firewall-add'); }}>Aggiungere una regola</button>
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={exportCsv}>Esportare</button>
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" disabled={!!fwBusy} onClick={() => setFwResetOpen(true)}>Ripristina le regole di origine</button>
                  <span className="nfx-tb__spacer" />
                  <div className="nfx-tb__search">
                    <Search size={16} />
                    <input placeholder="Cercare…" value={fwQ} onChange={(e) => setFwQ(e.target.value)} />
                  </div>
                </div>
                <table className="nfx-table">
                  <thead><tr><th>Tipo</th><th>Porta</th><th>IP/Ospite</th><th>Statuto</th><th>Descrizione</th><th className="nfx-td-actions" /></tr></thead>
                  <tbody>
                    {shown.map((r, i) => (
                      <tr key={r.id}>
                        <td>{r.tipo}</td>
                        <td className={r.porta === 'Tutti' || r.porta === '—' ? '' : 'nfx-cell-mono'}>{r.porta}</td>
                        <td className={r.host === 'Tutte' ? '' : 'nfx-cell-mono'}>{r.host}</td>
                        <td><span className={`nfx-fwstate nfx-fwstate--${r.enabled ? 'on' : 'off'}`}>{r.enabled ? 'Attivato' : 'Disattivato'}</span></td>
                        <td>{r.desc}</td>
                        <td className="nfx-td-actions">
                          <div className="nfx-fwmenu">
                            <button className="nfx-iconbtn" title="Azioni" aria-haspopup="true" disabled={!!fwBusy} onClick={() => setFwMenu(fwMenu === r.id ? null : r.id)}><MoreVertical size={18} /></button>
                            {fwMenu === r.id && (
                              <>
                                <div className="nfx-fwmenu__scrim" onClick={() => setFwMenu(null)} />
                                <ul className="nfx-fwmenu__list">
                                  <li><button disabled={!!fwBusy} onClick={() => fwEditRule(r)}>Modificare</button></li>
                                  {r.enabled ? (
                                    <li><button disabled={r.locked || !!fwBusy} onClick={() => { setFwMenu(null); setFwConfirm({ type: 'disable', id: r.id }); }}>Disattivare</button></li>
                                  ) : (
                                    <li><button disabled={!!fwBusy} onClick={() => { setFwMenu(null); fwToggleRule(r.id, true); }}>Attivare</button></li>
                                  )}
                                  <li><button disabled={r.locked || !!fwBusy} onClick={() => { setFwMenu(null); setFwConfirm({ type: 'delete', id: r.id }); }}>Eliminare</button></li>
                                </ul>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {shown.length === 0 && <tr><td colSpan={6}><div className="nfx-loading">Nessun risultato trovato per questi criteri.</div></td></tr>}
                  </tbody>
                </table>
              </div>
            )}
            <p className="nfx-dom-note" style={{ marginTop: 14 }}>Le regole sono applicate in tempo reale sul server. La porta 22 (SSH) è sempre mantenuta consentita; la porta 25 (SMTP) non può essere aperta.</p>

            {fwConfirm && (
              <div className="nfx-cfm-scrim" onClick={() => !fwBusy && setFwConfirm(null)}>
                <div className="nfx-cfm" onClick={(e) => e.stopPropagation()}>
                  <button className="nfx-cfm__close" onClick={() => setFwConfirm(null)} aria-label="Chiudi"><X size={13} /></button>
                  <div className="nfx-cfm__body">
                    <p className="nfx-del-p" style={{ marginTop: 0 }}>{fwConfirm.type === 'delete' ? 'Desidera veramente eliminare questa regola?' : 'Desidera veramente disattivare questa regola?'}</p>
                    <div className="nfx-cfm__btns">
                      <button className="nfx-cfm-btn nfx-cfm-btn--danger" disabled={!!fwBusy} onClick={fwConfirmRun}>{fwBusy ? 'Attendere…' : (fwConfirm.type === 'delete' ? 'Eliminare' : 'Disattivare')}</button>
                      <button className="nfx-cfm-btn nfx-cfm-btn--default" disabled={!!fwBusy} onClick={() => setFwConfirm(null)}>Annullare</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {fwResetOpen && (
              <div className="nfx-cfm-scrim" onClick={() => !fwBusy && setFwResetOpen(false)}>
                <div className="nfx-cfm" onClick={(e) => e.stopPropagation()}>
                  <button className="nfx-cfm__close" onClick={() => setFwResetOpen(false)} aria-label="Chiudi"><X size={13} /></button>
                  <div className="nfx-cfm__body">
                    <div className="nfx-del-title">Ripristinare le regole di origine?</div>
                    <p className="nfx-del-p">Tutte le regole personalizzate verranno rimosse e il firewall tornerà alla configurazione predefinita. La porta 22 (SSH) resterà sempre consentita.</p>
                    <div className="nfx-cfm__btns">
                      <button className="nfx-cfm-btn nfx-cfm-btn--default" disabled={!!fwBusy} onClick={() => setFwResetOpen(false)}>Annullare</button>
                      <button className="nfx-cfm-btn nfx-cfm-btn--danger" disabled={!!fwBusy} onClick={fwReset}>{fwBusy ? 'Attendere…' : 'Ripristina'}</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* ══════════ Firewall — Aggiungere una regola (pagina, stile del riferimento) ══════════ */}
      {sec === 'firewall-add' && !opInProgress && (() => {
        const appQ = fwAppQ.trim().toLowerCase();
        const appsShown = appQ ? FW_APPS.filter((a) => a.name.toLowerCase().includes(appQ)) : FW_APPS;
        const allShownOn = appsShown.length > 0 && appsShown.every((a) => fwAdd.apps.includes(a.name));
        const toggleApp = (name) => setFwAdd((s) => ({ ...s, apps: s.apps.includes(name) ? s.apps.filter((x) => x !== name) : [...s.apps, name] }));
        const toggleAll = () => setFwAdd((s) => {
          const names = appsShown.map((a) => a.name);
          return { ...s, apps: allShownOn ? s.apps.filter((x) => !names.includes(x)) : [...new Set([...s.apps, ...names])] };
        });
        return (
          <div className="nfx-card nfx-fwadd">
            <form onSubmit={(e) => { e.preventDefault(); fwSubmitAdd(); }}>
              {/* Porte da aprire */}
              <h2 className="nfx-fwadd__h">Porte da aprire</h2>
              <div className="nfx-fwadd__radios">
                {[['all', 'Tutte le porte'], ['application', 'Per una o più applicazioni'], ['manual', 'Selezione manuale']].map(([v, l]) => (
                  <label key={v} className="nfx-fwradio">
                    <input type="radio" name="fwportsel" checked={fwAdd.portSel === v} onChange={() => setFwAdd((s) => ({ ...s, portSel: v }))} />
                    <span>{l}</span>
                  </label>
                ))}
              </div>

              {fwAdd.portSel === 'application' && (
                <div className="nfx-fwadd__block">
                  <div className="nfx-fwadd__apptop">
                    <span className="nfx-fwadd__count">Applicazione/i selezionata/e : {fwAdd.apps.length}</span>
                    <div className="nfx-tb__search">
                      <input placeholder="CERCARE…" value={fwAppQ} onChange={(e) => setFwAppQ(e.target.value)} />
                      <Search size={16} />
                    </div>
                  </div>
                  <div className="nfx-fwadd__apps">
                    <table className="nfx-table">
                      <thead><tr>
                        <th style={{ width: 44 }}><input type="checkbox" checked={allShownOn} onChange={toggleAll} /></th>
                        <th>Nome</th><th>Porte</th><th>Protocollo</th>
                      </tr></thead>
                      <tbody>
                        {appsShown.map((app) => {
                          const on = fwAdd.apps.includes(app.name);
                          return (
                            <tr key={app.name} className="is-clickable" onClick={() => toggleApp(app.name)}>
                              <td><input type="checkbox" checked={on} readOnly /></td>
                              <td>{app.name}</td>
                              <td className="nfx-cell-mono">{app.ports.join(' ')}</td>
                              <td className="nfx-cell-mono">{app.proto}</td>
                            </tr>
                          );
                        })}
                        {appsShown.length === 0 && <tr><td colSpan={4}><div className="nfx-loading">Nessun risultato trovato per questi criteri.</div></td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {fwAdd.portSel === 'manual' && (
                <div className="nfx-fwadd__block">
                  <div className="nfx-fwadd__selwrap">
                    <select className="nfx-fwadd__msel" value={fwAdd.portType} onChange={(e) => setFwAdd((s) => ({ ...s, portType: e.target.value }))}>
                      <option value="ALL">Tutti</option><option value="TCP">TCP</option><option value="UDP">UDP</option><option value="ICMP">ICMP</option>
                    </select>
                  </div>
                  {fwAdd.portType !== 'ICMP' && (
                    <>
                      <div className="nfx-fwadd__radios nfx-fwadd__radios--inline" style={{ marginTop: 22 }}>
                        <label className="nfx-fwradio"><input type="radio" name="fwpst" checked={fwAdd.portSelType === 'select'} onChange={() => setFwAdd((s) => ({ ...s, portSelType: 'select' }))} /><span>Porte separate da una virgola</span></label>
                        <label className="nfx-fwradio"><input type="radio" name="fwpst" checked={fwAdd.portSelType === 'range'} onChange={() => setFwAdd((s) => ({ ...s, portSelType: 'range' }))} /><span>Gamma di porte</span></label>
                      </div>
                      {fwAdd.portSelType === 'select' ? (
                        <div className="nfx-fwadd__field" style={{ maxWidth: 700, marginTop: 20 }}>
                          <input className="nfx-fwadd__minput" placeholder="Porte separate da una virgola" value={fwAdd.ports} onChange={(e) => setFwAdd((s) => ({ ...s, ports: e.target.value }))} />
                        </div>
                      ) : (
                        <div className="nfx-fwadd__row" style={{ marginTop: 20 }}>
                          <div className="nfx-fwadd__field"><input className="nfx-fwadd__minput" placeholder="di" value={fwAdd.rangeStart} onChange={(e) => setFwAdd((s) => ({ ...s, rangeStart: e.target.value }))} /></div>
                          <div className="nfx-fwadd__field"><input className="nfx-fwadd__minput" placeholder="a" value={fwAdd.rangeStop} onChange={(e) => setFwAdd((s) => ({ ...s, rangeStop: e.target.value }))} /></div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Sorgente IP */}
              <h2 className="nfx-fwadd__h">Sorgente IP</h2>
              <div className="nfx-fwadd__radios">
                <label className="nfx-fwradio"><input type="radio" name="fwip" checked={fwAdd.ipType === 'all'} onChange={() => setFwAdd((s) => ({ ...s, ipType: 'all' }))} /><span>Tutte</span></label>
                <label className="nfx-fwradio"><input type="radio" name="fwip" checked={fwAdd.ipType === 'specific'} onChange={() => setFwAdd((s) => ({ ...s, ipType: 'specific' }))} /><span>IP specifici</span></label>
              </div>
              {fwAdd.ipType === 'specific' && (
                <div className="nfx-fwadd__block">
                  <div className="nfx-fwadd__radios nfx-fwadd__radios--inline">
                    {[['unique', 'IP unico'], ['subnetwork', 'In rete'], ['range', 'Gamma di indirizzo IP']].map(([v, l]) => (
                      <label key={v} className="nfx-fwradio"><input type="radio" name="fwipst" checked={fwAdd.ipSpecificType === v} onChange={() => setFwAdd((s) => ({ ...s, ipSpecificType: v }))} /><span>{l}</span></label>
                    ))}
                  </div>
                  {fwAdd.ipSpecificType === 'unique' && (
                    <div className="nfx-fwadd__field" style={{ maxWidth: 700, marginTop: 20 }}><input className="nfx-fwadd__minput" placeholder="Indirizzo IP" value={fwAdd.specificIp} onChange={(e) => setFwAdd((s) => ({ ...s, specificIp: e.target.value }))} /></div>
                  )}
                  {fwAdd.ipSpecificType === 'subnetwork' && (
                    <div className="nfx-fwadd__row" style={{ marginTop: 20 }}>
                      <div className="nfx-fwadd__field"><input className="nfx-fwadd__minput" placeholder="Indirizzo IP" value={fwAdd.specificIp} onChange={(e) => setFwAdd((s) => ({ ...s, specificIp: e.target.value }))} /></div>
                      <div className="nfx-fwadd__field"><input className="nfx-fwadd__minput" placeholder="Lunghezza del prefisso/suffisso" value={fwAdd.specificSubnet} onChange={(e) => setFwAdd((s) => ({ ...s, specificSubnet: e.target.value }))} /></div>
                    </div>
                  )}
                  {fwAdd.ipSpecificType === 'range' && (
                    <div className="nfx-fwadd__row" style={{ marginTop: 20 }}>
                      <div className="nfx-fwadd__field"><input className="nfx-fwadd__minput" placeholder="di" value={fwAdd.ipRangeStart} onChange={(e) => setFwAdd((s) => ({ ...s, ipRangeStart: e.target.value }))} /></div>
                      <div className="nfx-fwadd__field"><input className="nfx-fwadd__minput" placeholder="a" value={fwAdd.ipRangeStop} onChange={(e) => setFwAdd((s) => ({ ...s, ipRangeStop: e.target.value }))} /></div>
                    </div>
                  )}
                </div>
              )}

              {/* Descrizione */}
              <h2 className="nfx-fwadd__h">Descrizione</h2>
              <textarea className="nfx-fwadd__desc" value={fwAdd.description} onChange={(e) => setFwAdd((s) => ({ ...s, description: e.target.value }))} />

              <div className="nfx-fwadd__foot">
                <button type="submit" className="nfx-cfm-btn nfx-cfm-btn--primary" disabled={!!fwBusy}>{fwBusy ? 'Attendere…' : 'Convalidare'}</button>
                <button type="button" className="nfx-cfm-btn nfx-cfm-btn--default" disabled={!!fwBusy} onClick={() => goSec('firewall')}>Annullare</button>
              </div>
            </form>
          </div>
        );
      })()}

      {/* ══════════ Snapshot ══════════ */}
      {sec === 'snapshot' && !opInProgress && (
        <>
          <p className="nfx-dom-note" style={{ marginBottom: 16 }}>Uno snapshot permette di eseguire il backup dell&rsquo;intero server su uno spazio dedicato.</p>
          <div className="nfx-card nfx-card--pad" style={{ marginBottom: 20 }}>
            <div className="nfx-vps-snapuse">
              <div>Snapshot utilizzati : 0 / 1<div className="nfx-vps-snapbar"><span style={{ width: '0%' }} /></div></div>
              <button className="nfx-iconbtn" onClick={offer}><ShoppingCart size={18} /></button>
            </div>
          </div>
          <div className="nfx-card nfx-card--pad">
            <div className="nfx-cp-empty" style={{ border: 'none' }}>
              <Camera size={34} />
              <p><b>Nessuno snapshot per il momento</b></p>
              <p>Hai a disposizione uno snapshot gratuito per eseguire il backup dell&rsquo;intero server. Provalo!</p>
              <button className="nfx-btn-blue is-disabled" disabled style={{ marginTop: 8 }}>Crea uno snapshot</button>
            </div>
          </div>
        </>
      )}

      {/* ══════════ Domini associati (lista) ══════════ */}
      {sec === 'domains' && !opInProgress && (() => {
        const list = domini?.domini || [];
        const q = domQ.trim().toLowerCase();
        const shown = q ? list.filter((d) => d.nome.toLowerCase().includes(q)) : list;
        return (
          <div className="nfx-card">
            <div className="nfx-tb nfx-fwtb">
              <button className="nfx-cfm-btn nfx-cfm-btn--primary" onClick={() => { setDomSub(''); goSec('domains-add'); }}>Associare un dominio</button>
              <span className="nfx-tb__spacer" />
              <div className="nfx-tb__search">
                <Search size={16} />
                <input placeholder="Cercare…" value={domQ} onChange={(e) => setDomQ(e.target.value)} />
              </div>
            </div>
            <table className="nfx-table">
              <thead><tr><th>Nome</th><th>Tipo</th><th className="nfx-td-actions" /></tr></thead>
              <tbody>
                {dominiLoading ? (
                  <tr><td colSpan={3}><div className="nfx-loading">Verifica DNS in corso…</div></td></tr>
                ) : shown.length ? shown.map((d, i) => (
                  <tr key={i}>
                    <td className="nfx-cell-strong">{d.nome}</td>
                    <td>A</td>
                    <td className="nfx-td-actions" />
                  </tr>
                )) : (
                  <tr><td colSpan={3}><div className="nfx-loading">Nessun risultato trovato per questi criteri.</div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* ══════════ Associare un dominio (pagina) ══════════ */}
      {sec === 'domains-add' && !opInProgress && (
        <div className="nfx-card nfx-fwadd">
          <form onSubmit={(e) => { e.preventDefault(); flash('ok', 'Associazione dominio: funzione in arrivo.'); goSec('domains'); }}>
            <div className="nfx-fwadd__field" style={{ maxWidth: 360 }}>
              <input className="nfx-fwadd__minput" placeholder="Sotto-dominio" value={domSub} onChange={(e) => setDomSub(e.target.value)} autoFocus />
            </div>
            <div className="nfx-fwadd__foot">
              <button type="submit" className="nfx-cfm-btn nfx-cfm-btn--primary">Convalidare</button>
              <button type="button" className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => goSec('domains')}>Annullare</button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════ Manutenzione programmata ══════════ */}
      {sec === 'maintenance' && !opInProgress && (
        <>
          <div className="nfx-hdd-section-title" style={{ marginTop: 0 }}><Wrench size={18} /> Manutenzioni programmate</div>
          <div className="nfx-card nfx-card--pad">
            <div className="nfx-cp-empty" style={{ border: 'none' }}>
              <Wrench size={30} />
              <p><b>Nessuna manutenzione programmata</b></p>
              <p>Non sono previsti interventi di manutenzione su questa VPS. Eventuali finestre di manutenzione del nodo verranno comunicate via email e mostrate qui.</p>
            </div>
          </div>
        </>
      )}

      {/* ══════════ Reverse DNS ══════════ */}
      {sec === 'reverse-dns' && !opInProgress && (
        <div className="nfx-card nfx-card--pad">
          {rdnsLoading ? (
            <div className="nfx-loading">Caricamento reverse DNS…</div>
          ) : (
            <div className="nfx-rdns">
              <div className="nfx-rdns__row">
                <div className="nfx-rdns__ip">
                  <label className="nfx-rdns__lbl">IPv4</label>
                  <input className="nfx-inp" value={info.ipv4 || ''} disabled readOnly />
                </div>
                <span className="nfx-rdns__arrow"><ArrowRight size={18} /></span>
                <div className="nfx-rdns__host">
                  <label className="nfx-rdns__lbl">Nome dell&rsquo;host (FQDN)</label>
                  <input className="nfx-inp" value={rdnsV4} onChange={(e) => setRdnsV4(e.target.value)} placeholder="host.esempio.it" autoFocus />
                </div>
              </div>
              <div className="nfx-rdns__row">
                <div className="nfx-rdns__ip">
                  <label className="nfx-rdns__lbl">IPv6</label>
                  <input className="nfx-inp" value={info.ipv6 || ''} disabled readOnly />
                </div>
                <span className="nfx-rdns__arrow"><ArrowRight size={18} /></span>
                <div className="nfx-rdns__host">
                  <label className="nfx-rdns__lbl">Nome dell&rsquo;host (FQDN)</label>
                  <input className="nfx-inp" value={rdnsV6} onChange={(e) => setRdnsV6(e.target.value)} placeholder="host.esempio.it" />
                </div>
              </div>
              <div className="nfx-rdns__actions">
                <button className="nfx-btn-blue" onClick={saveReverseDns} disabled={rdnsBusy}>{rdnsBusy ? 'Registrazione…' : 'Registrare'}</button>
                <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => goSec('panel')}>Annullare</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════ Ripristinare il server (reinstall) ══════════ */}
      {sec === 'reinstall' && !opInProgress && (
        <>
          <div className="nfx-ri-warning">
            Attenzione! Stai per resettare il tuo server. Durante il processo, tutti i contenuti del volume dedicato al sistema operativo (/dev/sda, /dev/sda1 o /dev/vda) verranno eliminati definitivamente.
          </div>
          <div className="nfx-card nfx-card--pad">
            {osCat === null ? (
              <div className="nfx-loading">Caricamento…</div>
            ) : (
              <div className="nfx-ri-form">
                <div className="nfx-ri-group">
                  <label className="nfx-ri-label">Distribuzione / versione</label>
                  <div className="nfx-ri-row2">
                    <MatSelect
                      value={riDistro}
                      onChange={pickDistro}
                      placeholder="Distribuzione"
                      options={distroList.map((d) => ({ value: d.slug, label: d.nome }))}
                    />
                    <MatSelect
                      value={riVer}
                      onChange={setRiVer}
                      disabled={!riDistro}
                      placeholder="Versione"
                      options={riVersioni.map((v) => ({ value: String(v.id), label: `${v.nome}${v.consigliato ? ' — consigliata' : ''}` }))}
                    />
                  </div>
                </div>

                <div className="nfx-ri-group">
                  <label className="nfx-ri-label">Password di root</label>
                  <div className="nfx-ri-row2">
                    <input
                      type="password"
                      className="nfx-matsel__sel nfx-ri-input"
                      value={riPwd}
                      onChange={(e) => setRiPwd(e.target.value)}
                      placeholder="Almeno 8 caratteri"
                      autoComplete="new-password"
                    />
                  </div>
                  <p className="nfx-ri-hint">La password verrà impostata per l&rsquo;utente <b>root</b> del server reinstallato.</p>
                </div>

                <div className="nfx-cfm__btns" style={{ marginTop: 34 }}>
                  <button className="nfx-cfm-btn nfx-cfm-btn--danger" onClick={askReinstall} disabled={!riVer || riPwd.length < 8}>Re-inizializzare</button>
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => goSec('panel')}>Annullare</button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* ══════════ Disattivazione (termina) ══════════ */}
      {sec === 'termina' && !opInProgress && (
        <div className="nfx-term">
          <h1 className="nfx-term__title">Disattivazione del tuo prodotto {info.plan} : {v.nome_servizio}</h1>

          {/* Stepper (nascosto nella schermata di conferma) */}
          {tStep !== 'success' && (
          <div className="nfx-term__labels">
            <span className={`nfx-term__label${['motivo'].includes(tStep) ? ' is-current' : ''}`}>Motivo del recesso</span>
            <span className={`nfx-term__label${tStep === 'mail' ? ' is-current' : ''}`} style={{ visibility: tStep === 'motivo' ? 'hidden' : 'visible' }}>Verifica Mail</span>
            <span className={`nfx-term__label${['password', 'verifica-token'].includes(tStep) ? ' is-current' : ''}`} style={{ visibility: ['password', 'verifica-token'].includes(tStep) ? 'visible' : 'hidden' }}>Password</span>
          </div>
          )}
          {tStep !== 'success' && (
          <div className="nfx-term__bars">
            <span className="nfx-term__bar is-active" />
            <span className={`nfx-term__bar${tStep !== 'motivo' ? ' is-active' : ''}`} />
            <span className={`nfx-term__bar${['password', 'verifica-token'].includes(tStep) ? ' is-active' : ''}`} />
          </div>
          )}

          {tStep === 'success' && (
            <div className="nfx-term-success">
              <div className="nfx-term-success__content">
                <h2>Il prodotto {info.plan} {v.nome_servizio} è stato disattivato.</h2>
                <p>Nei prossimi minuti, tutti gli amministratori riceveranno una e-mail di conferma.</p>
                <button className="nfx-cfm-btn nfx-cfm-btn--primary" onClick={() => navigate(V2_BASE)}>Torna al manager</button>
              </div>
              <img className="nfx-term-success__img" src={EMPTY_ILLU} alt="" />
            </div>
          )}

          {tStep !== 'success' && (
          <div className="nfx-card nfx-card--pad" style={{ marginTop: 24 }}>
            {tErr && <div className="nfx-carderr" style={{ marginBottom: 16 }}>{tErr}</div>}

            {/* STEP 1 — Motivo */}
            {tStep === 'motivo' && (
              <>
                <h2 className="nfx-term__h2">Esprimi la tua opinione</h2>
                <div className="nfx-term__sub">Come giudichi questo prodotto? *</div>
                <div className="nfx-term__rates">
                  {Array.from({ length: 11 }, (_, i) => i).map((n) => (
                    <button key={n} type="button" className={`nfx-term__rate${tRating === n ? ' is-on' : ''}`} onClick={() => setTRating(n)}>{n}</button>
                  ))}
                </div>
                <div className="nfx-term__sub">Qual è il motivo del recesso? *</div>
                <div className="nfx-term__reasons">
                  {REASONS.map((rr) => (
                    <div key={rr.v}>
                      <label className="nfx-term__reason">
                        <button type="button" className={`nfx-pm-radio${tReason === rr.v ? ' is-on' : ''}`} onClick={() => setTReason(rr.v)} aria-pressed={tReason === rr.v} />
                        <span>{rr.t}</span>
                      </label>
                      {tReason === rr.v && (
                        <textarea className="nfx-term__msg" placeholder={rr.ph} value={tMsg} onChange={(e) => setTMsg(e.target.value)} />
                      )}
                    </div>
                  ))}
                </div>
                <div className="nfx-term__btns">
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => goSec('panel')}>Annullare</button>
                  <button className="nfx-cfm-btn nfx-cfm-btn--primary" onClick={termInviaRichiesta} disabled={tBusy || tRating == null || tReason == null}>{tBusy ? 'Attendere…' : 'Continua'}</button>
                </div>
              </>
            )}

            {/* STEP 2 — Verifica mail */}
            {tStep === 'mail' && (
              <>
                <h2 className="nfx-term__h2">Verifica il tuo indirizzo e-mail per finalizzare l&rsquo;eliminazione</h2>
                <p className="nfx-term__text">È stata inviata <strong>una e-mail di verifica</strong> all&rsquo;indirizzo <strong>{tEmail}</strong>. Clicca sul link in essa contenuto per continuare l&rsquo;eliminazione del tuo prodotto.</p>
                <div className="nfx-term__alert">
                  <span className="nfx-term__alert-ic"><Globe size={18} /></span>
                  <span>Non hai ricevuto nulla? Controlla la cartella della posta indesiderata o invia di nuovo una e-mail.</span>
                  <button className="nfx-acc-link" onClick={termInviaRichiesta} disabled={tBusy}>Inviare di nuovo la e-mail</button>
                </div>
                <div className="nfx-term__btns">
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => goSec('panel')}>Annullare</button>
                </div>
              </>
            )}

            {/* STEP 3 — Password */}
            {tStep === 'verifica-token' && <div className="nfx-loading">Verifica del link in corso…</div>}
            {tStep === 'password' && (
              <>
                <h2 className="nfx-term__h2">Disattiva il tuo prodotto {info.plan} {v.nome_servizio}.</h2>
                <div className="nfx-term__warns">
                  <div className="nfx-term__warn"><span className="nfx-term__warn-ic"><X size={14} /></span><strong>Tutti gli hosting e tutti i siti web installati su questo server verranno definitivamente rimossi e non saranno più accessibili.</strong></div>
                  <div className="nfx-term__warn"><span className="nfx-term__warn-ic"><X size={14} /></span><strong>I dati non saranno recuperabili.</strong></div>
                </div>
                <div className="nfx-ri-label" style={{ marginTop: 24 }}>Inserisci la password:</div>
                <input type="password" className="nfx-matsel__sel nfx-ri-input" style={{ maxWidth: 320 }} placeholder="Password di accesso *"
                  value={tPwd} onChange={(e) => setTPwd(e.target.value)} autoComplete="new-password" />
                <div className="nfx-term__btns">
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => goSec('panel')}>Annullare</button>
                  <button className="nfx-cfm-btn nfx-cfm-btn--danger" onClick={termConferma} disabled={tBusy || !tPwd}>{tBusy ? 'Attendere…' : 'Conferma la disattivazione'}</button>
                </div>
              </>
            )}
          </div>
          )}
        </div>
      )}

      {tagModal && (
        <div className="nfx-cfm-scrim" onClick={() => setTagModal(null)}>
          <div className="nfx-tagm" onClick={(e) => e.stopPropagation()}>
            {tagModal.view === 'select' ? (
              <>
                <div className="nfx-tagm__head">Etichetta</div>
                <div className="nfx-tagm__body">
                  <div className="nfx-tagm__field">
                    {dtags.map((t, ti) => (
                      <span key={ti} className="nfx-vps-tagchip" style={{ background: t.color }}>
                        {t.name}
                        <button className="nfx-tagm__chipx" onClick={() => removeTag(t.name)} aria-label="Rimuovi"><X size={12} /></button>
                      </span>
                    ))}
                    <input className="nfx-tagm__input" autoFocus placeholder="Seleziona o crea un tag"
                      value={tagModal.name}
                      onChange={(e) => setTagModal((m) => ({ ...m, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter' && tagModal.name.trim()) setTagModal((m) => ({ ...m, view: 'create' })); }} />
                  </div>
                  {tagModal.name.trim() && (
                    <button className="nfx-tagm__createlink" onClick={() => setTagModal((m) => ({ ...m, view: 'create' }))}>
                      <PlusCircle size={15} /> Crea il tag «{tagModal.name.trim()}»
                    </button>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="nfx-tagm__head">Crea un tag</div>
                <div className="nfx-tagm__body">
                  <div className="nfx-tagm__create">
                    <div className="nfx-tagm__colors">
                      {TAG_PALETTE.map((c) => (
                        <button key={c} className={`nfx-tagm__swatch${tagModal.color === c ? ' is-sel' : ''}`} style={{ background: c }}
                          onClick={() => setTagModal((m) => ({ ...m, color: c }))} aria-label={c} />
                      ))}
                    </div>
                    <input className="nfx-tagm__input" autoFocus placeholder="Nome del tag"
                      value={tagModal.name}
                      onChange={(e) => setTagModal((m) => ({ ...m, name: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') addTag(); }} />
                  </div>
                </div>
                <div className="nfx-tagm__foot">
                  <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={() => setTagModal((m) => ({ ...m, view: 'select' }))}>Annullare</button>
                  <button className="nfx-cfm-btn nfx-cfm-btn--primary" onClick={addTag} disabled={!tagModal.name.trim()}>Crea</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {upgradeOpen && (
        <div className="nfx-cfm-scrim" onClick={() => setUpgradeOpen(false)}>
          <div className="nfx-upg" onClick={(e) => e.stopPropagation()}>
            <button className="nfx-upg__close" onClick={() => setUpgradeOpen(false)} aria-label="Chiudi"><X size={14} /></button>
            <h1 className="nfx-upg__title">Passa da VPS Lite al nostro VPS Cloud più performante e dai più forza ai tuoi progetti</h1>
            <div className="nfx-upg__feats">
              <div className="nfx-upg__feat">
                <img className="nfx-upg__img" src="/assets/assets/images/cloud/ico-upgrade-snapshots.svg" alt="" />
                <div className="nfx-upg__ftxt">Guardati le spalle con gli snapshot</div>
              </div>
              <div className="nfx-upg__feat">
                <img className="nfx-upg__img" src="/assets/assets/images/cloud/ico-upgrade-production.svg" alt="" />
                <div className="nfx-upg__ftxt">Metti i tuoi progetti in produzione</div>
              </div>
              <div className="nfx-upg__feat">
                <img className="nfx-upg__img" src="/assets/assets/images/cloud/ico-upgrade-sla.svg" alt="" />
                <div className="nfx-upg__ftxt">SLA garantito al 99,99%</div>
              </div>
            </div>
            <div className="nfx-upg__btns">
              <button className="nfx-vps-upgrade" onClick={goUpgrade}>Aggiornare</button>
              <button className="nfx-cfm-btn nfx-cfm-btn--default" onClick={offer}>Per più informazioni</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════ DETTAGLIO SERVER DEDICATO (gestione manuale) ═══════════════════ */
export const DedicatedDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const offer = () => window.open('https://neoflux.it', '_blank');

  useEffect(() => {
    let alive = true; setLoading(true);
    api.get(`/api/cloud/dedicated/${id}`)
      .then((r) => { if (alive) setD(r.data); })
      .catch(() => { if (alive) setD(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) return <div className="nfx-card" style={{ marginTop: 8 }}><div className="nfx-loading">Caricamento…</div></div>;
  if (!d || d.errore) return <div className="nfx-card nfx-card--pad" style={{ marginTop: 8 }}>Server non trovato.</div>;

  const info = dedInfo(d);
  const st = STATE_TAG(d.stato);

  return (
    <div>
      <div className="nfx-crumb">
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE)} aria-label="Home"><Home size={16} /></button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <button className="nfx-crumb__home" onClick={() => navigate(V2_BASE + '/cloud-dedicated/dedicated')} style={{ fontSize: 13.5, width: 'auto' }}>Server Dedicati</button>
        <span className="nfx-crumb__sep"><ChevronRight size={15} /></span>
        <span className="nfx-crumb__cur">{d.nome_servizio}</span>
      </div>

      <div className="nfx-vps-grid">
        <div className="nfx-card" style={{ overflow: 'hidden' }}>
          <div className="nfx-vps-hero nfx-vps-hero--ded">
            <div className="nfx-vps-hero__ic" style={{ color: '#2e4a3a' }}><Server size={30} /></div>
            <div className="nfx-vps-hero__name">{d.nome_servizio}</div>
            <div className="nfx-vps-hero__host">Host name : {info.hostname}</div>
          </div>
          <div className="nfx-card--pad">
            <div className="nfx-vps-info">
              <div className="nfx-vps-info__k">Stato</div>
              <div className="nfx-vps-info__v"><span className={`nfx-st nfx-st--${st.cls}`}>{st.label}</span></div>
              <div className="nfx-vps-info__k">Offerta</div>
              <div className="nfx-vps-info__v">{info.plan}</div>
              <div className="nfx-vps-info__k">IPv4</div>
              <div className="nfx-vps-info__v"><span className="nfx-cell-mono">{info.ipv4}</span></div>
              <div className="nfx-vps-info__k">Datacenter</div>
              <div className="nfx-vps-info__v">{info.datacenter || '—'}</div>
              <div className="nfx-vps-info__k">Scadenza</div>
              <div className="nfx-vps-info__v">{fmtDate(d.data_scadenza)}</div>
            </div>
          </div>
        </div>

        <div>
          <div className="nfx-card nfx-card--pad" style={{ marginBottom: 20 }}>
            <div className="nfx-hdd-cardtitle">Configurazione hardware</div>
            <div className="nfx-vps-info">
              <div className="nfx-vps-info__k">CPU</div>
              <div className="nfx-vps-info__v">{info.cpu || '—'}</div>
              <div className="nfx-vps-info__k">RAM</div>
              <div className="nfx-vps-info__v">{info.ram || '—'}</div>
              <div className="nfx-vps-info__k">Archiviazione</div>
              <div className="nfx-vps-info__v">{info.disk || '—'}</div>
            </div>
          </div>
          <div className="nfx-card nfx-card--pad">
            <div className="nfx-hdd-cardtitle">Gestione</div>
            <p className="nfx-dom-note">Questo server dedicato è gestito direttamente dal team NeoFlux. Per interventi su hardware, reinstallazione o configurazione apri una richiesta di assistenza.</p>
            <div className="nfx-hdd-actions" style={{ marginTop: 14 }}>
              <button className="nfx-hdd-action" onClick={() => navigate(V2_BASE + '/support')}><Wrench size={16} /> Apri una richiesta di assistenza</button>
              <button className="nfx-hdd-action" onClick={offer}><RefreshCw size={16} /> Rinnova il server</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ServerDedicatiPage = () => <CloudDashboard tab="dedicated" />;
export const VpsPage = () => <CloudDashboard tab="vps" />;
export const StoragePage = () => <CloudDashboard tab="storage" />;
export const NasPage = () => <CloudDashboard tab="nas" />;
export const HousingPage = () => <CloudDashboard tab="housing" />;
